// Berlin, "Light festival at the Gate": an October night on Pariser Platz during the city's festival of light. We stand
// on the square's axis and face west. Back to front: a clear autumn sky with a thin crescent going down in the west,
// slow clouds and the festival's sky beams fanning out from behind the gate -> hooded crows streaming to their roost in
// the Tiergarten -> through the five passages: the Tiergarten's trees, Strasse des 17. Juni running to the tiny golden
// Victory Column on the axis, the cross traffic on Ebertstrasse (a yellow double-decker, a police van) -> the
// Brandenburg Gate, big and close: six Doric columns a side, five passages (the middle one widest), the triglyph
// frieze, the attic with its relief, and the Quadriga facing us; coloured projections wash over the columns and the
// attic in slow acts, then a countdown and a bright finale with confetti once per loop, after which the show rests and
// the gate stands in its own warm floodlight -> the low wings with their colonnades and the two gatehouses, crows on a
// roof -> plain sandstone houses left and right, the right one under scaffolding with a banner (the billboard) -> the
// paved square, damp and mirroring the projections, full of people: spectators raising phones, photographers with
// tripods, a busker with a guitar and a ring of listeners, a couple dancing, a soap-bubble artist and children, a
// pretzel seller with a basket bike, a hot-drink cart, a sausage seller under his umbrella, an advertising column, a
// steady stream of walkers, a pedicab, a bike tour, a marching brass band -> linden boughs from Unter den Linden
// framing the near corners, dropping yellow leaves at three depths -> vignette.
// render(t, { pitch }) swaps the banner's ad, render(t, { billboard: false }) leaves the banner and its lamps out,
// render(t, { crowd }) thins the people on the square, down to nobody at 0 (see CROWD).
//
// Timeline (loop seconds; everything else runs all the time: the projections, the walkers, the busker, the dancers,
// the children, a sweep of soap bubbles every 20 s, camera flashes, phones going up):
//   6-34 pedicab ->        12-32 a customer at the drink cart   16-50 crows fly over      36-40 applause, 41-48 a coin
//   46-58 the drum major marks time at the left edge, heads turn, 58-104 the BRASS BAND marches through
//   46-64 a customer at the sausage grill   72-92 drink cart    84-96 police van behind the gate   96-100 applause
//   100-122 a customer at the pretzel bike  104-117 the sky beams come on one by one, 117-142 they open into a FAN
//   124-152 pedicab <-     132-152 drink cart   138-166 crows   146-170 double-decker   148-166 sausage grill
//   150-184 a bike tour -> 156-160 applause, 161-168 a coin
//   178-194 COUNTDOWN: the lamps and beams dim, the columns charge one by one, the phones come up
//   194-216 FINALE: cuts on a beat, white flashes, confetti cannons at 194 / 199.5 / 205 / 210.5, crossing beams
//   196-216 drink cart     208-230 pretzel bike   216-240 the show rests: the gate in its own floodlight
//   220-238 sausage grill  228-232 applause
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, inEvent, cycle, hash, rng, bayer, hex,
  makeCanvas, rect, ditherFill, makeHalo, drawHalo, text, makePitch } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                          // this scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout (5 px to the metre at the gate) ================= */
const G = 188;                      // the gate's ground line: Pariser Platz starts here
const CX = 240;                     // the gate's axis; we stand on it and face west
const COL_W = 9;                    // 1.73 m columns
const COLS = [161, 189, 217, 254, 282, 310];                    // left edge of each column
const PASS = [[170, 188], [198, 216], [226, 253], [263, 281], [291, 309]];   // the five passages [x0, x1]: 3.80 m, 5.65 m in the middle
const GX0 = 161, GX1 = 318;         // the central block
const CAP = 121;                    // top of the 13.5 m columns
const ENT = 104;                    // top of the entablature (cornice)
const ATTIC = 87;                   // top of the attic
const PED = 83;                     // top of the Quadriga's stepped pedestal
const QTIP = 51;                    // the eagle on the Quadriga's standard, ~26 m up
const PJ = { x: 156, y: 80, w: 168, h: 109 };                   // what the projectors cover: the central block's front
const WING = { roof: 147, col: 152, lx0: 83, lx1: 160, rx0: 319, rx1: 396 };   // the low wings: roof line, column tops
const HOUSE = { top: 97, lx1: 82, rx0: 397 };                   // Haus Sommer (left, south) and Haus Liebermann (right, north)
const VP = { x: 240, y: 162 };      // vanishing point of the square and of Strasse des 17. Juni
const BB = { x: 401, y: 112, w: 48, h: 20 };                    // the banner on the scaffold (the pitch is painted exactly here); ends by x 450 so a 16:10 crop keeps it whole
const MOON = { x: 352, y: 36 };
// a person standing with their feet on row y is this tall (the gate's own scale at its foot, bigger towards us)
const tall = y => Math.round(10 + (y - 192) * 0.33);
// The share of the crowd the scene shows by default: 1 = everyone, 0.5 = about half, just above 0 = only the cast the
// scene's story needs (the sellers and their customers, the busker with the listener who brings him a coin, the
// dancing couple, the bubble artist and a child, the pedicab, the bike tour, the brass band). Tune this number for the
// city; render(t, { crowd }) overrides it for one frame. Exactly 0 means nobody: the cast goes too, with everything
// that is only there because of them, and the square is left to its lamps, the lit stalls, the crows and the show.
const CROWD = 0.5;
// Everyone has a fixed rank in [0, 1) and is drawn while rank < crowd; rank 0 is the cast that stays at every share
// above 0. The golden-ratio sequence over a group's index thins the group evenly instead of emptying one corner first.
const rankOf = (i, off = 0) => frac((i + 0.5) * 0.6180339887 + off);

/* ================= Pixel helpers ================= */
function painter(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data, cache = {};
  const put = (i, y, col, a = 255) => {
    i = Math.round(i); y = Math.round(y);
    if (i < 0 || y < 0 || i >= w || y >= h) return;
    const rgb = cache[col] || (cache[col] = hex(col)), o = (y * w + i) * 4;
    d[o] = rgb[0]; d[o + 1] = rgb[1]; d[o + 2] = rgb[2]; d[o + 3] = a;
  };
  const alpha = (i, y) => (i < 0 || y < 0 || i >= w || y >= h) ? 0 : d[(y * w + i) * 4 + 3];
  const box = (x0, y0, bw, bh, col) => { for (let y = y0; y < y0 + bh; y++) for (let i = x0; i < x0 + bw; i++) put(i, y, col); };
  return { c, x, w, h, put, alpha, box, done: () => { x.putImageData(img, 0, 0); return c; } };
}
// integer Bresenham line through put(x, y)
function pline(put, x0, y0, x1, y1, col) {
  x0 = Math.round(x0); y0 = Math.round(y0); x1 = Math.round(x1); y1 = Math.round(y1);
  const dx = Math.abs(x1 - x0), dy = -Math.abs(y1 - y0), sx = x0 < x1 ? 1 : -1, sy = y0 < y1 ? 1 : -1;
  let err = dx + dy;
  for (;;) {
    put(x0, y0, col);
    if (x0 === x1 && y0 === y1) break;
    const e2 = 2 * err;
    if (e2 >= dy) { err += dy; x0 += sx; }
    if (e2 <= dx) { err += dx; y0 += sy; }
  }
}
const cput = (i, y, col) => { ctx.fillStyle = col; ctx.fillRect(i, y, 1, 1); };
const line = (x0, y0, x1, y1, col) => pline(cput, x0, y0, x1, y1, col);
// sprite from rows of characters and a colour key ('.' or unknown = transparent)
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = painter(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
function stamp(p, rows, key, x0, y0) {
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.put(x0 + i, y0 + y, col); } });
}
// dithered soft blob for steam
function makePuff(r, light, dark) {
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r) / (r + 0.5);
    if (d < 1 && (1 - d) * 1.8 > bayer(i, y) * 0.95) p.put(i, y, i + y < r * 1.7 ? light : dark);
  }
  return p.done();
}
const mixCache = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (!mixCache[k]) {
    const A = hex(a), B = hex(b);
    mixCache[k] = '#' + [0, 1, 2].map(j => Math.round(A[j] + (B[j] - A[j]) * t).toString(16).padStart(2, '0')).join('');
  }
  return mixCache[k];
}
const darker = (c, a = 0.38) => mixHex(c, '#05040c', a);
const lighter = (c, a = 0.3) => mixHex(c, '#fff4e0', a);
const ramp = (pal, v, i, y, spread = 1) => pal[clamp(Math.floor(v * (pal.length - 1) + (bayer(i, y) - 0.5) * spread + 0.5), 0, pal.length - 1)];
function drawScroll(cv, y, k, t) {
  const off = Math.floor(frac(k * t / LOOP) * W);
  ctx.drawImage(cv, off - W, y); ctx.drawImage(cv, off, y);
}

/* ================= Sky: a clear October night, the city's glow low down, the festival's haze round the gate ================= */
const SKY = ['#05061a', '#070922', '#0a0d2b', '#0d1134', '#11163d', '#161b46', '#1c214f', '#232756', '#2b2d5c', '#353361', '#403965'];
function renderSky() {
  return ditherFill(W, H, SKY, (i, y) => {
    let v = Math.pow(Math.min(1, y / 176), 1.5) * 0.85;
    v += Math.max(0, 1 - Math.hypot((i - CX) / 260, (y - 150) / 110)) * 0.22;     // light scattered above the square
    return v;
  });
}
const STARS = [];
{ const r = rng(907); for (let i = 0; i < 70; i++) STARS.push({ x: Math.floor(r() * W), y: 1 + Math.floor(r() * 92), k: 16 + Math.floor(r() * 60), ph: r(), b: 0.3 + r() * 0.6, big: r() < 0.12 }); }
function renderMoon() {                               // a thin waxing crescent going down in the west
  const p = painter(17, 17);
  for (let y = 0; y < 17; y++) for (let i = 0; i < 17; i++) {
    const d = Math.hypot(i - 8, y - 8), d2 = Math.hypot(i - 4.6, y - 6.2);
    if (d <= 7.3 && d2 > 6.7) p.put(i, y, d > 6.3 ? '#d8d2b8' : '#fff8dc');
    else if (d <= 7.3 && bayer(i, y) < 0.16) p.put(i, y, '#1e2450');               // earthshine
  }
  return p.done();
}

/* ----- clouds: tileable banks, moonlit tops, bellies warmed by the city ----- */
const CLOUD_DEFS = [
  { seed: 11, y: 0, h: 46, banks: 3, k: 1, sx: 1.4, pal: { top: '#3a4372', body: '#1c2248', core: '#161b3e', bot: '#4a3c66', bot2: '#2c2a52' } },
  { seed: 37, y: 26, h: 50, banks: 4, k: 2, sx: 1.2, pal: { top: '#4a4f7e', body: '#262a54', core: '#1e2248', bot: '#6a4a70', bot2: '#3a325c' } },
  { seed: 53, y: 60, h: 44, banks: 3, k: 3, sx: 1.5, pal: { top: '#565a88', body: '#30325e', core: '#282a52', bot: '#7c5678', bot2: '#463a64' } },
];
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = r() * W, bw = 60 + r() * 100, base = h * (0.45 + r() * 0.35), np = 5 + Math.floor(r() * 5);
    for (let i = 0; i < np; i++) {
      const rx = (7 + r() * 12) * def.sx, ry = 3 + r() * 5;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const p of puffs) {
      let dx = x - p.x; dx -= Math.round(dx / W) * W;
      const dy = y - p.y;
      let v = 1 - (dx * dx) / (p.rx * p.rx) - (dy * dy) / (p.ry * p.ry);
      if (y > p.base) v -= (y - p.base) * 0.4;
      if (v > best) best = v;
    }
    best += 0.14 * Math.sin(TAU * 9 * x / W + y * 0.37 + def.seed) * Math.sin(TAU * 4 * x / W - y * 0.21);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = painter(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.put(x, y, col);
  }
  return { c: p.done(), y: def.y, k: def.k };
}

/* ----- the festival's sky beams, from behind the gate (Platz des 18. Maerz) ----- */
// Four of them idle all night, swaying; once per loop all six come on one by one and open into a sweeping fan
// (FAN), they go dark for the countdown and cross fast in the finale (see the show's timeline further down).
const FAN = { a: 104, b: 142, open: 117 };            // build-up from 104 s, the fan from 117 s
const DARK = { a: 178, b: 194 };                      // the countdown: beams and lamps dim
const FINALE = { a: 194, b: 216 };
const BEAMS = [
  { x: 132, col: '#a8f000', idle: 0 }, { x: 179, col: '#bcd4ff', idle: 0.6 }, { x: 207, col: '#ff7ad8', idle: 0.3 },
  { x: 272, col: '#5ce8ff', idle: 0.3 }, { x: 300, col: '#bcd4ff', idle: 0.6 }, { x: 348, col: '#a8f000', idle: 0 },
];
const BEAM_Y = 178, BEAM_LEN = 280;
function beamState(i, t) {
  const B = BEAMS[i], sp = (i - 2.5) * 0.3;
  let ang = sp * 0.55 + 0.2 * wave(t, 3 + (i % 3), i * 0.23), a = B.idle * (0.85 + 0.15 * wave(t, 7 + i, i * 0.31));
  const wf = env(t, FAN.a + i * 1.6, FAN.b, 2.5);                             // one after the other
  if (wf > 0) {
    const open = smooth(FAN.open, FAN.open + 6, t), sweep = 0.3 * Math.sin(TAU * (t - FAN.open) / 12) * open;
    ang = lerp(ang, sp * (0.1 + 0.9 * open) + sweep, wf); a = lerp(a, 1, wf);
  }
  const wn = env(t, FINALE.a, FINALE.b, 1.5);
  if (wn > 0) { ang = lerp(ang, sp * 1.1 * Math.cos(TAU * (t - FINALE.a) / 5.5 + i * 0.9), wn); a = lerp(a, 1.15, wn); }
  a *= 1 - env(t, DARK.a, DARK.b + 1.5, 2.5);
  return { ang, a };
}
function drawBeams(t) {
  ctx.globalCompositeOperation = 'lighter';
  for (let i = 0; i < BEAMS.length; i++) {
    const { ang, a } = beamState(i, t);
    if (a < 0.03) continue;
    const B = BEAMS[i], s = Math.sin(ang), c = Math.cos(ang), ex = B.x + s * BEAM_LEN, ey = BEAM_Y - c * BEAM_LEN;
    ctx.fillStyle = B.col;
    for (const [hw, al] of [[11, 0.085], [4.5, 0.13]]) {
      ctx.globalAlpha = Math.min(1, al * a);
      ctx.beginPath(); ctx.moveTo(B.x - 1.5, BEAM_Y); ctx.lineTo(ex - c * hw, ey - s * hw); ctx.lineTo(ex + c * hw, ey + s * hw);
      ctx.lineTo(B.x + 1.5, BEAM_Y); ctx.closePath(); ctx.fill();
    }
  }
  ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
}

/* ----- hooded crows on their way to roost in the Tiergarten: two flocks per loop, scouts ahead of each ----- */
const FLOCKS = [{ a: 16, b: 50, n: 34, seed: 71 }, { a: 138, b: 166, n: 22, seed: 72 }];
const CROWS = [];
for (const F of FLOCKS) {
  const r = rng(F.seed);
  for (let i = 0; i < F.n; i++) {
    const scout = i < 3, dur = 13 + r() * 5, t0 = scout ? F.a + i * 1.4 : F.a + 6 + r() * (F.b - F.a - dur - 6);
    CROWS.push({ t0, dur, y0: 6 + r() * 58, x1: 60 + r() * 150, y1: 122 + r() * 10, bow: (r() - 0.5) * 30, ph: r(), fl: 5 + r() * 2 });
  }
}
function drawCrows(t) {
  ctx.fillStyle = '#03030a';
  for (const c of CROWS) {
    const u = (t - c.t0) / c.dur;
    if (u <= 0 || u >= 1) continue;
    const e = u * (2 - u) * 0.55 + u * 0.45;                                    // they slow as they near the trees
    const x = Math.round(lerp(W + 8, c.x1, e)), y = Math.round(lerp(c.y0, c.y1, e) + Math.sin(u * Math.PI) * c.bow);
    const big = u < 0.45, up = frac(t * c.fl / 2 + c.ph) < 0.5;
    if (big) {                                                                  // 7 px wingspan, flapping
      ctx.fillRect(x - 1, y, 3, 1); ctx.fillRect(x, y + 1, 1, 1);
      if (up) { ctx.fillRect(x - 3, y - 1, 2, 1); ctx.fillRect(x + 2, y - 1, 2, 1); }
      else { ctx.fillRect(x - 3, y + 1, 2, 1); ctx.fillRect(x + 2, y + 1, 2, 1); }
    } else if (u < 0.8) {
      ctx.fillRect(x, y, 1, 1); ctx.fillRect(x - 1, y + (up ? -1 : 0), 1, 1); ctx.fillRect(x + 1, y + (up ? -1 : 0), 1, 1);
    } else ctx.fillRect(x, y, up ? 2 : 1, 1);
  }
}
function drawSky(t) {
  ctx.drawImage(SKY_C, 0, 0);
  for (const s of STARS) {
    ctx.globalAlpha = s.b * (0.5 + 0.5 * wave(t, s.k, s.ph));
    ctx.fillStyle = s.big ? '#fff6e0' : '#cfd8ff';
    ctx.fillRect(s.x, s.y, 1, 1);
  }
  ctx.globalAlpha = 1;
  halo(HL.moon, MOON.x, MOON.y, 0.8);
  ctx.drawImage(MOON_C, MOON.x - 8, MOON.y - 8);
  for (const L of CLOUDS) drawScroll(L.c, L.y, L.k, t);
  drawBeams(t);
  drawCrows(t);
}

/* ================= Beyond the gate: the Tiergarten, Strasse des 17. Juni, the Victory Column on the axis ================= */
// Seen through the five passages and over the wings' roofs. The avenue runs dead straight to the Grosser Stern 2 km off,
// so the column stands on the vanishing point, about 12 px tall at this distance; Ebertstrasse crosses just behind the gate.
const EBERT = 176;                                    // the cross street's row
const AVE_LAMPS = [1, 1.5, 2.2, 3.2, 4.6, 6.6, 9.5, 14].map(d => ({ dx: 31 / d, y: Math.round(VP.y - 15 / d), d }));
function treeTop(x) {
  const dx = Math.abs(x - VP.x);
  const base = VP.y - Math.min(31, 3 + dx * 0.44);
  return Math.round(base + 2.2 * Math.sin(x * 0.37) + 1.6 * Math.sin(x * 0.83 + 2) + (hash(x >> 1, 3, 201) - 0.5) * 2.4);
}
const WOODS = ['#080b18', '#0c1120', '#121828', '#1a2030'];
const AUTUMN = ['#3a2a18', '#5e4018', '#8a5e1c', '#b8862a', '#e0b040'];
function renderFar() {
  const p = painter(W, H), put = p.put;
  for (let x = WING.lx0; x <= WING.rx1; x++) {
    const dx = Math.abs(x - VP.x), top = treeTop(x), roadHalf = y => 3 + (y - VP.y) * 3.3;
    for (let y = top; y <= G + 1; y++) {
      let col;
      if (y < VP.y || (dx > roadHalf(y) && y < EBERT - 2)) {                     // the woods
        const v = 0.25 + 0.5 * hash(x >> 1, y >> 1, 202) + (y === top ? 0.3 : 0);
        col = WOODS[clamp(Math.floor(v * 3 + bayer(x, y) - 0.5), 0, 3)];
        if (dx > 80 && hash(x, y >> 1, 206) < 0.14) col = y < top + 4 ? '#3a301c' : '#262214';     // the city's glow on yellow crowns
        for (const L of AVE_LAMPS) {                                             // lamplight on the yellow leaves
          const d = Math.hypot(dx - L.dx - 2 / L.d, (y - L.y) * 1.3) * L.d / 6;
          if (d < 1 && hash(x, y, 203) < (1 - d) * 0.7) col = AUTUMN[clamp(Math.floor((1 - d) * 3.4 + hash(x, y, 204) - 0.6), 0, 3)];
        }
      } else if (y < EBERT - 2) {                                                // the avenue's asphalt
        const lane = Math.abs(dx - roadHalf(y) * 0.5) < 0.6 && ((y - VP.y) & 1) === 0;
        col = dx < 0.6 ? '#3a3c52' : lane ? '#4a4c62' : y - VP.y < 3 ? '#1e2238' : '#161a2e';
      } else if (y <= EBERT + 2) col = y === EBERT - 2 || y === EBERT + 2 ? '#3c3e58' : '#1a1c30';   // Ebertstrasse, kerbs
      else {                                                                      // Platz des 18. Maerz
        const v = 0.3 + 0.5 * (y - EBERT) / 12 + (hash(x >> 2, y, 205) - 0.5) * 0.2;
        col = ['#20233c', '#2a2d48', '#343754', '#3e415e'][clamp(Math.floor(v * 3 + bayer(x, y) - 0.5), 0, 3)];
      }
      put(x, y, col);
    }
  }
  for (const L of AVE_LAMPS) for (const s of [-1, 1]) {                           // the avenue's lamps, two rows to the horizon
    const x = Math.round(VP.x + s * L.dx);
    put(x, L.y, L.d < 5 ? '#fff0c0' : '#ffd890');
    if (L.d < 3.5) { put(x, L.y + 1, '#ffc060'); for (let y = L.y + 2; y < VP.y + 15 / L.d * 0.4; y++) put(x, y, '#2a2c40'); }
  }
  // the Victory Column: red granite shaft on its drum, gilded Victoria on top
  for (let y = 159; y <= 161; y++) for (let i = -1; i <= 1; i++) put(VP.x + i, y, i < 0 ? '#b89a86' : i ? '#5a4850' : '#8a7068');
  for (let y = 152; y <= 158; y++) put(VP.x, y, (y & 1) ? '#c8a488' : '#a88470');
  put(VP.x, 151, '#e8b840'); put(VP.x - 1, 150, '#ffd860'); put(VP.x, 150, '#fff0a0'); put(VP.x + 1, 150, '#e8b840'); put(VP.x, 149, '#ffe890');
  // traffic lights where Ebertstrasse meets the square (their lamps are drawn per frame)
  for (const x of [205, 274]) for (let y = 166; y < EBERT - 2; y++) put(x, y, '#0a0c16');
  return p.done();
}

/* ----- far traffic: Ebertstrasse's cross traffic, a yellow double-decker, a police van, the avenue's lights ----- */
const CARS = [];
{ const r = rng(211); for (let i = 0; i < 12; i++) CARS.push({ n: 9 + Math.floor(r() * 8), ph: r(), dir: i & 1 ? 1 : -1, col: ['#1c1e2c', '#3a3c4c', '#50202a', '#e8e4dc', '#22304a'][Math.floor(r() * 5)] }); }
const BUS = { per: 120, a: 26, b: 50 };               // tau = t % 120
const POLICE = { a: 84, b: 96 };
const BUS_ROWS = ['.yyyyyyyyyyyy.', 'ywwywwywwywwyy', 'yyyyyyyyyyyyyy', 'ywwywwywwywkyy', 'yyyyyyyyyyyyyh', 'yyyyyyyyyyyyyy', '.kk......kk...'];
const BUS_KEY = { y: '#f2c81e', w: '#fff2b0', k: '#16161e', h: '#ffffff' };
const VAN_ROWS = ['...b......', '.wwwwwww..', 'wwkwkwkww.', 'wggggggggh', '.kk...kk..'];
const VAN_KEY = { w: '#e8ecf0', k: '#1a2030', g: '#3aa0d8', h: '#fff8d0', b: '#2a50ff' };
const FAR_RANK = [0, 1, 2, 3, 4, 5, 6, 7, 8].map(i => rankOf(i, 0.75));   // the nine people beyond the gate
function drawFarLife(t, crowd) {
  // Strasse des 17. Juni: headlights coming out of the distance on the left, tail lights leaving on the right
  for (let i = 0; i < 6; i++) {
    const { u } = cycle(t, 10 + i, i * 0.37), e = u * u;
    const y = Math.round(VP.y + 1 + e * 10), x = Math.round(VP.x + (i & 1 ? 1 : -1) * (1 + e * 16 + (i >> 1)));
    cput(x, y, i & 1 ? '#ff4030' : '#fff4d0');
    if (e > 0.4 && !(i & 1)) cput(x + 1, y, '#ffe8b0');
  }
  for (let i = 0; i < CARS.length; i++) {                                         // Ebertstrasse
    const c = CARS[i], { u } = cycle(t, c.n, c.ph), x = Math.round(c.dir > 0 ? 140 + u * 200 : 340 - u * 200), y = EBERT + (c.dir > 0 ? 0 : -1);
    ctx.fillStyle = c.col; ctx.fillRect(x - 2, y - 1, 5, 2);
    cput(x + 2 * c.dir + c.dir, y, '#fff4d0'); cput(x - 2 * c.dir, y, '#ff3020');
  }
  const tau = t % BUS.per;
  if (tau >= BUS.a && tau < BUS.b) {
    const x = Math.round(lerp(134, 332, (tau - BUS.a) / (BUS.b - BUS.a)));
    ctx.drawImage(BUS_C, x, EBERT - 6);
    halo(HL.farWarm, x + 7, EBERT - 3, 0.7);
  }
  if (t >= POLICE.a && t < POLICE.b) {
    const x = Math.round(lerp(336, 130, (t - POLICE.a) / (POLICE.b - POLICE.a)));
    ctx.drawImage(VAN_C, x, EBERT - 5);
    if (frac(t * 3.5) < 0.5) { cput(x + 6, EBERT - 5, '#bcd0ff'); halo(HL.blue, x + 6, EBERT - 5, 1); }
  }
  const red = step(t + 3, 16) & 1;                                                // the lights change every 15 s
  for (const x of [205, 274]) cput(x, red ? 166 : 168, red ? '#ff3828' : '#40ff80');
  halo(HL.column, VP.x, 151, 0.75 + 0.2 * wave(t, 9, 0.2));
  if (frac(t / 5 + 0.2) < 0.07) cput(VP.x, 149, '#ffffff');                       // the gilded Victoria glints
  // people on the far side of the gate, small and dark against the lit paving
  for (let i = 0; i < 9; i++) {
    if (FAR_RANK[i] >= crowd) continue;
    const { u } = cycle(t, 3 + (i % 4), i * 0.113 + 0.05), dir = i & 1 ? 1 : -1;
    const x = Math.round(dir > 0 ? 150 + u * 180 : 330 - u * 180), y = 181 + (i % 3) * 2, f = step(t + i * 0.3, 720) & 1;
    ctx.fillStyle = '#0a0b18';
    ctx.fillRect(x, y - 6, 2, 4); ctx.fillRect(x, y - 2, 1, 2); ctx.fillRect(x + 1 + (f ? 0 : dir), y - 2, 1, 2);
    cput(x + (dir > 0 ? 1 : 0), y - 7, '#58483e');
  }
}

/* ================= The Brandenburg Gate ================= */
// The central block's front is painted three times from the same elevation: in warm floodlit sandstone (what shows
// where no projection falls), as white-lit greys, which the projection's colours are multiplied into each frame, and
// crisp in the gate's own floodlight (SAND), which the show rests on once a loop: pale stone under warm white-gold
// lamps, each column lit across its face with a hard-edged shadow down its right side.
const STONE = ['#241c2c', '#3e3040', '#5e4a4c', '#84685a', '#aa8a6c', '#ccae86', '#ead4a8'];
const LUM = ['#1c1a2a', '#3c3a4a', '#63616f', '#8f8d98', '#bab8c0', '#dedcde', '#fbf8f2'];
const SAND = ['#2c2232', '#54404a', '#86684e', '#b49062', '#d8b67c', '#f0d69e', '#fff2cc'];
function drawFront(put, pal, crisp = false) {
  const S = (i, y, v) => put(i, y, ramp(pal, v, i, y, crisp ? 0.35 : 0.9));
  COLS.forEach(x0 => {                                                           // six columns: capital, tapered fluted shaft, base
    for (let y = CAP; y <= G; y++) {
      const cr = y - CAP, base = y >= G - 2;
      let a = x0, b = x0 + COL_W - 1;
      if (cr === 0 || base) { a--; b++; } else if (cr > 1 && y < CAP + 36) { a++; b--; }
      for (let i = a; i <= b; i++) {
        const u = (i - a) / (b - a);
        // crisp: a lit rim, the lit face with its fluting, then a hard shadow, each on a whole step of the palette
        let v = crisp ? (u < 0.12 ? 4 / 6 : u < 0.62 ? 1 - ((i - a) & 1) / 6 : u < 0.76 ? 3 / 6 : 2 / 6)
          : 0.3 + 0.64 * Math.sin(Math.PI * (0.12 + 0.68 * u));
        if (cr === 0) v = 0.9 - u * 0.2; else if (cr === 1) v = 0.72 - u * 0.2; else if (cr === 2) v -= crisp ? 2 / 6 : 0.24;
        else if (base) v = y === G - 2 ? 0.84 - u * 0.2 : 0.62 - u * 0.25;
        else if (!crisp && (i - a) & 1) v -= 0.08;                               // fluting
        S(i, y, v);
      }
    }
  });
  for (let y = ENT; y < CAP; y++) {                                               // entablature: cornice, triglyph frieze, architrave
    const r = y - ENT, out = r < 3 ? 4 : 2;
    for (let i = GX0 - out; i <= GX1 + out; i++) {
      let v;
      if (r < 3) v = [0.96, 0.8, 0.24][r];
      else if (r < 10) {
        const m = (i - (GX0 - 2)) % 10, mi = Math.floor((i - (GX0 - 2)) / 10);
        if (m < 3) v = m === 1 ? 0.28 : 0.76;                                     // triglyph
        else if (r === 3 || r === 9) v = 0.6;
        else v = hash(i, y, 300 + mi) < 0.5 ? 0.94 : 0.44;                         // a metope's relief: centaurs and Lapiths
      } else v = [0.94, 0.78, 0.74, 0.72, 0.72, 0.66, 0.3][r - 10];
      if (i > GX1 - 2) v -= 0.12;
      S(i, y, v);
    }
  }
  for (let y = ATTIC; y < ENT; y++) for (let i = GX0; i <= GX1; i++) {            // the attic and its relief panel
    const r = y - ATTIC;
    let v = r === 0 ? 0.96 : r === 1 ? 0.78 : r === 16 ? 0.4 : r === 15 ? 0.56 : 0.7;
    if (r >= 3 && r <= 14 && i >= 197 && i <= 282) {
      if (r === 3 || r === 14 || i === 197 || i === 282) v = 0.4;
      else {                                                                      // the procession of the goddess of peace
        const f = Math.floor((i - 198) / 3), top = 5 + Math.floor(hash(f, 1, 302) * 3), c = (i - 198) % 3;
        const head = hash(f, 2, 302) < 0.5 ? 0 : 1;                               // each figure: a head, then shoulders and robe
        v = c < 2 && hash(f, 0, 302) < 0.72 && r >= top && (r > top || c === head) ? (r === top ? 1 : c ? 4 / 6 : 5 / 6) : 0.48;
      }
    } else if (r >= 4 && r <= 13 && ((i >= GX0 + 5 && i <= 191) || (i >= 288 && i <= GX1 - 5))) {
      const edge = r === 4 || r === 13 || i === GX0 + 5 || i === 191 || i === 288 || i === GX1 - 5;
      v = edge ? 0.54 : 0.64;                                                     // sunk side panels
    }
    if (i > GX1 - 2) v -= 0.14; else if (i < GX0 + 2) v += 0.08;
    S(i, y, v);
  }
  for (const [x0, x1, y0, y1] of [[204, 275, 85, 86], [220, 259, PED, 84]])       // the Quadriga's low stepped pedestal
    for (let y = y0; y <= y1; y++) for (let i = x0; i <= x1; i++) S(i, y, y === y0 ? 0.9 : i > x1 - 3 ? 0.5 : i < x0 + 2 ? 0.8 : 0.7);
}
function renderFront(pal, crisp = false) {
  const p = painter(PJ.w, PJ.h);
  drawFront((i, y, col) => p.put(i - PJ.x, y - PJ.y, col), pal, crisp);
  return p.done();
}
// the strip of a passage's outer wall we see from the axis (the passages are 11 m deep): 7, 4 and 1 px
const WALL_W = [7, 4, 1, 4, 7];
const WARM = ['#2a1c20', '#4a3228', '#745234', '#a07c48', '#c8a464', '#e8c888', '#ffe6b0'];
function wingColumns(put, xs, y0, w) {
  for (const x0 of xs) for (let y = y0; y <= G; y++) for (let i = 0; i < w; i++) {
    const cap = y === y0, base = y >= G - 1;
    let v = 0.26 + 0.3 * Math.sin(Math.PI * (0.15 + 0.65 * i / (w - 1))) + Math.pow((y - y0) / (G - y0), 1.5) * 0.34;   // lit from the ground
    if (cap) v = 0.6; else if (y === y0 + 1) v -= 0.16; else if (base) v = 0.74 - i * 0.06;
    put(x0 + i, y, ramp(STONE, v, x0 + i, y, 0.8));
    if (cap || base) { put(x0 - 1, y, STONE[5]); put(x0 + w, y, STONE[2]); }
  }
}
function drawWing(put, mir) {
  const X = x => mir ? W - 1 - x + 0 : x;                                         // the left wing mirrors the right one
  const P = (x, y, col) => put(X(x), y, col);
  // the open hall between the gate and the gatehouse (set back): a dark room, warm light low down, three columns
  for (let y = WING.roof; y <= G; y++) for (let x = 319; x <= 350; x++) {
    const r = y - WING.roof;
    let col;
    if (r < 2) col = ramp(STONE, r === 0 ? 0.6 : 0.4, x, y, 0.6);
    else if (r < 5) col = ramp(STONE, r === 4 ? 0.22 : 0.4, x, y, 0.6);
    else col = ramp(WARM, 0.08 + 0.42 * Math.pow((y - WING.roof) / (G - WING.roof), 2.2), x, y, 1.2);
    P(x, y, col);
  }
  wingColumns(P, [322, 332, 342], WING.col, 4);
  // the gatehouse: a little Doric temple front, six columns, a low pediment, a lit door and windows behind the columns
  const gc = 373.5;
  for (let y = 138; y <= G; y++) for (let x = 351; x <= 396; x++) {
    const r = y - WING.roof;
    let col = null;
    if (y < WING.roof) {                                                          // pediment
      const hw = (y - 138) / (WING.roof - 138) * 24.5;
      if (Math.abs(x - gc) <= hw) col = ramp(STONE, Math.abs(x - gc) > hw - 1.2 || y === WING.roof - 1 ? 0.62 : 0.36 + (x < gc ? 0.05 : -0.05), x, y, 0.7);
    } else if (r < 5) col = ramp(STONE, [0.7, 0.52, 0.26, 0.46, 0.34][r], x, y, 0.6);
    else {                                                                        // the wall behind the columns
      const door = Math.abs(x - gc) < 3.6 && y > 164, win = (Math.abs(x - 362) < 2.6 || Math.abs(x - 385) < 2.6) && y > 160 && y < 174;
      col = door ? (y === 165 ? '#3a2418' : '#1c1218') : win ? (y < 165 ? '#ffe2a0' : '#f0b060')
        : ramp(STONE, 0.28 + 0.3 * (y - WING.roof) / (G - WING.roof), x, y, 0.8);
    }
    if (col) P(x, y, col);
  }
  wingColumns(P, [352, 360, 368, 376, 384, 392], WING.col, 4);
  for (let x = 349; x <= 398; x++) { P(x, G - 0, ramp(STONE, 0.74, x, G, 0.5)); P(x, G + 1, STONE[2]); }   // the steps
}
function renderGateBase() {
  const p = painter(W, H), put = p.put;
  drawWing(put, false); drawWing(put, true);
  // inside the passages: the ceiling, the lit outer wall with its Hercules reliefs, a lamp's warm pool low down
  PASS.forEach(([x0, x1], k) => {
    const ww = WALL_W[k], left = k < 2 || k === 2, right = k > 2 || k === 2;
    for (let y = CAP; y <= G; y++) for (let x = x0; x <= x1; x++) {
      const inL = left && x < x0 + ww, inR = right && x > x1 - ww;
      if (y < CAP + 4) { put(x, y, ramp(WARM, 0.1 + 0.07 * (y - CAP) + (((x - x0) % 5 === 0) ? -0.06 : 0), x, y, 0.8)); continue; }
      if (!inL && !inR) continue;
      const d = inL ? (x - x0) / ww : (x1 - x) / ww;
      let v = 0.34 + 0.4 * (y - CAP) / (G - CAP) - d * 0.16;
      if (ww > 3 && y > CAP + 14 && y < CAP + 30 && d > 0.2 && d < 0.75) v += (hash(x, y, 310) < 0.5 ? 0.14 : -0.1);   // relief
      put(x, y, ramp(WARM, v, x, y, 0.6));
    }
  });
  drawFront(put, STONE);
  return p.done();
}

/* ----- the Quadriga: four horses abreast pulling Victoria's chariot, facing us (bronze gone green, floodlit) ----- */
// One dense group as it reads from the square: the horses' chests touch, heads up on short thick necks, the outer two
// turned out, each with a foreleg raised mid-stride; behind them the chariot's rim and Victoria with her wings up,
// holding out her standard: the Iron Cross in its oak wreath under the eagle. Digits are QC shades, '.' is empty.
const QC = ['#0a1a20', '#163834', '#2a6250', '#46927a', '#78c49c', '#c2ecbe', '#fff2c8'];
const QUAD = { x: CX - 22, y: QTIP - 1, w: 44, h: PED - QTIP + 1 };
const QUAD_ROWS = [
  '............................................',
  '...........4.....3..........................',
  '...........44.5.32..........................',
  '............44532...........................',
  '.............443....4..3....................',
  '............4.6.2..45..33...................',
  '............46662.454..332..................',
  '............4.6.2.543..322..................',
  '.............432..443..322..................',
  '..............3...34.32.22..................',
  '..............3....3.54.21..................',
  '..............3....334322...................',
  '..............54444455431...................',
  '...........3.33.....4542......3.3...........',
  '.....3.3..45433.....4542......3454..3.3.....',
  '....44443.55433.....4532......3455.34444....',
  '..4554412.454232....5442....2.2454.2144554..',
  '.4554.4432454.322..45432...223.4542344.4554.',
  '.453..443235..4322.455321.2234..532344..354.',
  '..2...443322..4432156655412344..223344...2..',
  '.....4443321.444332144431233444.1233444.....',
  '....544433215444322133221223444512334445....',
  '....5544332255443322.10.2233445522334455....',
  '....4554332245543322.10.2233455422334554....',
  '....4444332144443321.10.1233444412334444....',
  '....3444332.3444332..10..2334443.2334443....',
  '.....454.43..454.43..10..34.454..34.454.....',
  '.....45..43..45..43..10..34..54..34..54.....',
  '.....43..43..43..43......34..34..34..34.....',
  '.....3...42..3...42......24...3..24...3.....',
  '.........42......42......24......24.........',
  '.........42......42......24......24.........',
  '.........21......21......12......12.........',
];
function renderQuadriga(key) {
  const p = painter(QUAD.w, QUAD.h);
  stamp(p, QUAD_ROWS, key, 0, 0);
  return p.done();
}

/* ================= The projection show ================= */
// Six slow acts, then the countdown (the columns charge one by one while the square's lamps dim) and the finale
// (hard cuts on a beat, white flashes, strobing columns), then the show rests: for 'gold' the gate stands in its own
// warm floodlight (GATE_SUN) and fades back into the first act.
// Each act is a few pre-rendered tiles scrolled by whole pixels; the frame's pattern is multiplied into the white-lit
// stone (GATE_LUM) on a scratch canvas and masked to it, so only the gate's front takes the light.
const ACTS = [[0, 'rainbow'], [26, 'harlequin'], [56, 'aurora'], [86, 'mosaic'], [116, 'confetti'], [148, 'grid'],
  [DARK.a, 'count'], [FINALE.a, 'finale'], [FINALE.b, 'gold'], [LOOP, 'rainbow']];
const ACT_COL = { rainbow: '#ff8a6a', harlequin: '#ff3aa8', aurora: '#2ae0c0', mosaic: '#ffc23a', confetti: '#6a7aff',
  grid: '#a8f000', count: '#3a3ab0', finale: '#ffffff', gold: '#ffdc9c' };
const CUT = (FINALE.b - FINALE.a) / 16;               // the finale's beat: 16 cuts
const CUT_ORDER = ['harlequin', 'grid', 'rainbow', 'confetti', 'aurora', 'mosaic', 'harlequin', 'rainbow'];
function actAt(t) {
  let i = 0;
  while (t >= ACTS[i + 1][0]) i++;
  const next = ACTS[i + 1], hard = next[1] === 'finale';
  return { a: ACTS[i][1], b: next[1], blend: hard ? 0 : smooth(next[0] - 3, next[0], t) };
}
const finaleFlash = t => { const f = frac((t - FINALE.a) / CUT); return t >= FINALE.a && t < FINALE.b && f < 0.4 ? Math.pow(1 - f / 0.4, 2) : 0; };
const HUES = ['#ff3a5c', '#ff9a2a', '#ffe03a', '#4ae06a', '#2ab8ff', '#a05aff', '#ff3a5c'];
let PAT;
function renderPatterns() {
  const P = {};
  P.rainbow = ditherFill(180, PJ.h, HUES, (i, y) => frac((i + y * 0.35) / 180));
  { const p = painter(48, 48), C = ['#ff3aa8', '#5a2ad0', '#2ae0e0', '#ffd23a'];
    for (let y = 0; y < 48; y++) for (let i = 0; i < 48; i++) {
      const cx = i >> 4 & 1, cy = Math.floor(y / 24), d = Math.abs((i % 16) - 7.5) / 8 + Math.abs((y % 24) - 11.5) / 12;
      const k = (cx + cy * 2 + (i >= 32 ? 1 : 0)) & 3;
      p.put(i, y, d < 0.94 ? (d < 0.3 ? lighter(C[k], 0.45) : C[k]) : d < 1.06 ? '#1a0a3a' : C[(k + 2) & 3]);
    }
    P.harlequin = p.done(); }
  P.aurora = ditherFill(PJ.w, 60, ['#0a2a7a', '#1a6ad0', '#2ae0c0', '#a8f000', '#2ae0c0', '#1a6ad0', '#0a2a7a'],
    (i, y) => frac(y / 60 + 0.14 * Math.sin(TAU * i / 84) + 0.05 * Math.sin(TAU * i / 28 + 1)));
  P.mosaic = [401, 402].map(seed => {
    const p = painter(PJ.w, PJ.h), C = ['#e83a3a', '#ff8a2a', '#ffd23a', '#3a9aff', '#3ad07a', '#c04ae0', '#fff0c0'];
    for (let y = 0; y < PJ.h; y++) for (let i = 0; i < PJ.w; i++) {
      const row = Math.floor(y / 9), bx = Math.floor((i + (row & 1) * 3) / 7);
      p.put(i, y, (i + (row & 1) * 3) % 7 === 0 || y % 9 === 0 ? '#2a1430' : C[Math.floor(hash(bx, row, seed) * 7)]);
    }
    return p.done(); });
  P.confetti = [411, 412].map((seed, li) => {
    const p = painter(84, 84), r = rng(seed), C = ['#ff5aa8', '#ffe03a', '#5cf0ff', '#ffffff', '#a8f000', '#ff8a3a'];
    if (!li) p.box(0, 0, 84, 84, '#1a2278');
    for (let n = 0; n < (li ? 16 : 26); n++) {
      const x = Math.floor(r() * 84), y = Math.floor(r() * 84), col = C[Math.floor(r() * 6)], kind = r();
      const shape = kind < 0.3 ? ['.#.#.', '#####', '#####', '.###.', '..#..'] : kind < 0.6 ? ['.#.', '###', '.#.'] : kind < 0.85 ? ['##', '##'] : ['###', '###', '###'];
      shape.forEach((row, j) => { for (let i = 0; i < row.length; i++) if (row[i] === '#') p.put((x + i) % 84, (y + j) % 84, col); });
    }
    return p.done(); });
  { const p = painter(14, 14);
    for (let y = 0; y < 14; y++) for (let i = 0; i < 14; i++) p.put(i, y, i === 0 && y === 0 ? '#f4ffd0' : i === 0 || y === 0 ? '#a8f000' : (i === 1 || y === 1 || i === 13 || y === 13) ? '#1e5a2a' : '#062a30');
    P.grid = p.done(); }
  return P;
}
const sc = (t, k, size) => Math.floor(frac(k * t / LOOP) * size);       // whole-pixel scroll, k (integer) tiles per loop
function tile(pc, cv, ox, oy) {
  const w = cv.width, h = cv.height;
  for (let y = -((h - oy % h) % h); y < PJ.h; y += h) for (let x = -((w - ox % w) % w); x < PJ.w; x += w) pc.drawImage(cv, x, y);
}
function drawPattern(pc, id, t, calm) {
  if (id === 'rainbow') tile(pc, PAT.rainbow, sc(t, 4, 180), 0);
  else if (id === 'harlequin') tile(pc, PAT.harlequin, sc(t, 10, 48), sc(t, 5, 48));
  else if (id === 'aurora') tile(pc, PAT.aurora, 0, 60 - sc(t, 8, 60));
  else if (id === 'mosaic') {
    const ga = pc.globalAlpha, ox = sc(t, 2, PJ.w);
    tile(pc, PAT.mosaic[0], ox, 0);
    pc.globalAlpha = ga * (0.5 + 0.5 * wave(t, 30, 0)); tile(pc, PAT.mosaic[1], ox, 0); pc.globalAlpha = ga;
  } else if (id === 'confetti') { tile(pc, PAT.confetti[0], 0, sc(t, 12, 84)); tile(pc, PAT.confetti[1], sc(t, 3, 84), sc(t, 7, 84)); }
  else if (id === 'grid') {
    const ga = pc.globalAlpha;
    tile(pc, PAT.grid, sc(t, 24, 14), sc(t, 12, 14));
    const bx = Math.round(frac(t / 6) * (PJ.w + 40)) - 20, by = Math.round(frac(t / 8 + 0.3) * (PJ.h + 30)) - 15;
    pc.fillStyle = '#eaffb0';
    pc.globalAlpha = ga * 0.35; pc.fillRect(bx - 9, 0, 18, PJ.h); pc.fillRect(0, by - 5, PJ.w, 10);
    pc.globalAlpha = ga * 0.8; pc.fillRect(bx - 2, 0, 4, PJ.h); pc.fillRect(0, by - 1, PJ.w, 2);
    pc.globalAlpha = ga;
  } else if (id === 'count') {
    const ga = pc.globalAlpha, s = t - DARK.a - 2;                       // each column takes 2 s to charge, from 180 s
    pc.fillStyle = '#17153e'; pc.fillRect(0, 0, PJ.w, PJ.h);
    COLS.forEach((x0, k) => {
      const u = clamp((s - k * 2) / 2, 0, 1), hgt = Math.round(u * (G - CAP + 1));
      if (hgt <= 0) return;
      const full = u >= 1, blink = t > FINALE.a - 2.2 && !calm ? (frac(t * 2.5) < 0.5 ? 1 : 0.4) : 0.8 + 0.2 * wave(t, 240, k / 6);
      pc.globalAlpha = ga * (full ? blink : 1);
      pc.fillStyle = full ? '#f8fff0' : '#a8f000';
      pc.fillRect(x0 - PJ.x - 1, G - PJ.y + 1 - hgt, COL_W + 2, hgt);
    });
    pc.globalAlpha = ga; pc.fillStyle = '#a8f000';                        // a progress line along the frieze
    pc.fillRect(GX0 - PJ.x - 2, ENT - PJ.y + 3, Math.round(clamp(s / 12, 0, 1) * (GX1 - GX0 + 5)), 7);
  } else if (id === 'finale') {
    const q = (t - FINALE.a) / CUT, j = Math.floor(q), f = q - j, s = (t - FINALE.a) * 26, kind = CUT_ORDER[j % CUT_ORDER.length];
    if (kind === 'rainbow') tile(pc, PAT.rainbow, Math.floor(s * 2) % 180, 0);
    else if (kind === 'harlequin') tile(pc, PAT.harlequin, Math.floor(s) % 48, Math.floor(s * 0.5) % 48);
    else if (kind === 'aurora') tile(pc, PAT.aurora, 0, 60 - Math.floor(s) % 60);
    else if (kind === 'mosaic') tile(pc, PAT.mosaic[j & 1], Math.floor(s) % PJ.w, 0);
    else if (kind === 'confetti') { tile(pc, PAT.confetti[0], 0, Math.floor(s) % 84); tile(pc, PAT.confetti[1], 0, Math.floor(s * 1.7) % 84); }
    else tile(pc, PAT.grid, Math.floor(s * 0.5) % 14, Math.floor(s * 0.5) % 14);
    if (calm) return;
    const ga = pc.globalAlpha, st = Math.floor(t * 2.5), k = Math.floor(hash(st, 5, 431) * 6);   // one column at a time goes white
    pc.fillStyle = '#ffffff';
    pc.fillRect(COLS[k] - PJ.x - 1, CAP - PJ.y, COL_W + 2, G - CAP + 1);
    if (f < 0.4) { pc.globalAlpha = ga * Math.pow(1 - f / 0.4, 2) * 0.9; pc.fillRect(0, 0, PJ.w, PJ.h); pc.globalAlpha = ga; }
  } else { pc.fillStyle = '#ffe4b4'; pc.fillRect(0, 0, PJ.w, PJ.h); }     // gold: GATE_SUN goes over it (drawGate)
}
let PJ_C, PJ_X, GATE_LUM, GATE_SUN, GATE_BASE, QUAD_C, QUAD_W;
const PASS_LAMP = PASS.map(([x0, x1]) => (x0 + x1 + 1) >> 1);
function drawGate(t, calm) {
  const { a, b, blend } = actAt(t), pc = PJ_X;
  pc.globalCompositeOperation = 'source-over'; pc.globalAlpha = 1;
  drawPattern(pc, a, t, calm);
  if (blend > 0) { pc.globalAlpha = blend; drawPattern(pc, b, t, calm); pc.globalAlpha = 1; }
  pc.globalCompositeOperation = 'multiply'; pc.drawImage(GATE_LUM, 0, 0);
  pc.globalCompositeOperation = 'destination-in'; pc.drawImage(GATE_LUM, 0, 0);
  const sun = a === 'gold' ? 1 - blend : b === 'gold' ? blend : 0;               // the gate in its own light
  pc.globalCompositeOperation = 'source-over';
  if (sun > 0) { pc.globalAlpha = sun; pc.drawImage(GATE_SUN, 0, 0); pc.globalAlpha = 1; }
  // the light the gate throws back into the haze, in the act's colour
  halo(HL.wash[a], CX, 138, (1 - blend) * 0.9); if (blend > 0) halo(HL.wash[b], CX, 138, blend * 0.9);
  ctx.drawImage(GATE_BASE, 0, 0);
  PASS_LAMP.forEach((x, k) => halo(HL.passage, x, G - 10, 0.8 + 0.08 * wave(t, 40 + k * 7, k / 5)));   // warm light in the passages
  for (const x of [335, 373, W - 1 - 335, W - 1 - 373]) halo(HL.wing, x, G - 12, 0.85);
  ctx.drawImage(PJ_C, PJ.x, PJ.y);
  // the Quadriga: floodlit from the attic, white-hot on the finale's flashes
  const fl = calm ? 0 : finaleFlash(t);
  halo(HL.quad, CX, PED - 12, 0.75 + 0.15 * wave(t, 11, 0.3) + fl * 0.6);
  ctx.drawImage(QUAD_C, QUAD.x, QUAD.y);
  if (fl > 0.02) { ctx.globalAlpha = Math.min(1, fl); ctx.drawImage(QUAD_W, QUAD.x, QUAD.y); ctx.globalAlpha = 1; }
}

/* ================= The houses beside the gate: plain sandstone, equal height; the right one under scaffolding ================= */
const HST = ['#1c1826', '#2c2634', '#3e3644', '#544a54', '#6c6064', '#867a74'];
const WIN_ROWS = [106, 123, 140, 157];               // four upper floors, 10 px windows
const HWIN = [];                                      // windows that switch on and off: { id, x, y, n, off, tv }
const DECKS = [103, 120, 137, 154, 171];              // the scaffold's plank decks
const NET = { x0: HOUSE.rx0 + 1, x1: W - 1, y0: 99, y1: G - 2 };
function drawHouse(p, x0, x1, r, scaffolded) {
  const put = p.put;
  for (let y = HOUSE.top - 7; y <= G; y++) for (let x = x0; x <= x1; x++) {
    const attic = y < HOUSE.top;
    if (attic && (x < x0 + 4 || x > x1 - 4)) continue;                            // the top floor sits back
    let v = 0.2 + 0.5 * Math.pow((y - 90) / (G - 90), 1.6) + (hash(x, y, 501) - 0.5) * 0.05;
    if (y === HOUSE.top || y === HOUSE.top - 7) v += 0.3; else if (y === HOUSE.top + 1) v -= 0.14;   // cornices
    if (WIN_ROWS.some(wy => y === wy + 12)) v += 0.12;                             // string courses
    if (y > 168 && y < 171) v += 0.16;
    put(x, y, ramp(HST, v, x, y, 0.9));
  }
  const axes = [];
  for (let x = x0 + 5; x + 6 <= x1 - 3; x += 12) axes.push(x);
  for (const wy of WIN_ROWS) for (const x of axes) {
    const v = r(), lit = !scaffolded && v < 0.3, toggle = !scaffolded && !lit && v < 0.62;
    for (let j = 0; j < 10; j++) for (let i = 0; i < 6; i++) {
      const frame = i === 0 || i === 5 || j === 0, bar = i === 3 || j === 4;
      put(x + i, wy + j, frame ? HST[4] : lit ? (bar ? '#a8703a' : j < 4 ? '#ffe2a0' : '#f2b25e') : (bar ? '#1e1c2c' : j < 3 ? '#262842' : '#12121e'));
    }
    for (let i = -1; i <= 6; i++) put(x + i, wy + 10, HST[5]);
    if (toggle) HWIN.push({ id: HWIN.length, x: x + 1, y: wy + 1, n: 3 + Math.floor(r() * 5), off: r(), tv: r() < 0.25 });
  }
  for (let x = x0 + 9; x + 4 <= x1 - 5; x += 12) for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++)   // the set-back top floor
    put(x + i, HOUSE.top - 5 + j, !scaffolded && hash(x, 2, 502) < 0.35 ? '#f6c070' : '#14141f');
  for (const x of axes) for (let y = 173; y <= G; y++) for (let i = 0; i < 7; i++) {      // tall ground-floor openings
    const frame = i === 0 || i === 6 || y === 173;
    put(x + i, y, frame ? HST[5] : scaffolded ? '#16141e' : y < 177 ? '#ffe8b0' : (i === 3 ? '#b87a3a' : '#f4b860'));
  }
}
function renderHouses() {
  const p = painter(W, H), put = p.put, r = rng(511);
  drawHouse(p, 0, HOUSE.lx1, r, false);
  drawHouse(p, HOUSE.rx0, W - 1, r, true);
  { let k = 0; for (const w of HWIN) w.rank = hash(w.id, 3, 8) < 0.4 ? rankOf(k++, 0.25) : 0; }   // the people at the windows thin with the crowd
  // the scaffold: a green safety net over the whole front, standards, ledgers and plank decks, a ladder, a debris chute
  for (let y = NET.y0; y <= NET.y1; y++) for (let x = NET.x0; x <= NET.x1; x++) {
    const fold = Math.sin(x * 0.21 + Math.sin(y * 0.07) * 1.6), on = ((x + y) & 1) === 0 || fold > 0.5;
    if (on) put(x, y, fold > 0.75 ? '#3a8a70' : fold > 0 ? '#28685a' : '#1a4840');
  }
  for (let x = HOUSE.rx0 + 2; x < W; x += 13) for (let y = 92; y <= G; y++) { put(x, y, y < 150 ? '#70748a' : '#9a9eae'); put(x + 1, y, '#3a3e50'); }
  for (const dy of DECKS) for (let x = HOUSE.rx0; x < W; x++) {
    put(x, dy, '#b89a6a'); put(x, dy + 1, '#6a543c'); put(x, dy - 6, (x & 1) ? '#70748a' : '#50546a');   // deck, guard rail
  }
  for (let k = 0; k < DECKS.length - 1; k++) {                                     // diagonal braces, alternating
    const xa = HOUSE.rx0 + 2 + 13 * (k % 2 ? 4 : 1), xb = xa + 13;
    pline(put, k % 2 ? xb : xa, DECKS[k] + 2, k % 2 ? xa : xb, DECKS[k + 1] - 1, '#585c72');
  }
  for (let y = DECKS[3] + 2; y < G; y++) { put(HOUSE.rx0 + 30, y, '#a8acbc'); put(HOUSE.rx0 + 34, y, '#70748a'); if ((y & 3) === 0) for (let i = 31; i < 34; i++) put(HOUSE.rx0 + i, y, '#8a8ea0'); }
  for (let y = G - 7; y <= G; y++) for (let x = HOUSE.rx0 - 1; x < W; x++)          // the site hoarding at its foot, red and white
    put(x, y, y === G - 7 ? '#a8a8b4' : (((x >> 3) + (y > G - 4 ? 1 : 0)) & 1) ? '#9a3430' : '#b8b4ac');
  return p.done();
}
const SITE_LAMPS = [HOUSE.rx0 + 4, HOUSE.rx0 + 44, W - 6];   // amber warning lamps on the hoarding
function drawHouseLife(t, crowd) {
  for (const w of HWIN) {
    const on = hash(w.id + 60, Math.floor(frac(t / LOOP + w.off) * w.n), 6) < 0.5;
    if (!on) continue;
    if (w.tv) { ctx.fillStyle = ['#7aa0ff', '#a8c8ff', '#5a78d8', '#c8e0ff'][Math.floor(hash(w.id, step(t, 960), 7) * 4)]; ctx.fillRect(w.x, w.y, 4, 8); continue; }
    ctx.fillStyle = '#ffe2a0'; ctx.fillRect(w.x, w.y, 4, 3);
    ctx.fillStyle = '#f2b25e'; ctx.fillRect(w.x, w.y + 3, 4, 6);
    ctx.fillStyle = '#a8703a'; ctx.fillRect(w.x + 2, w.y, 1, 9);
    if (hash(w.id, 3, 8) < 0.4 && w.rank < crowd) { ctx.fillStyle = '#3a2418'; ctx.fillRect(w.x + 1, w.y + 4, 2, 5); ctx.fillRect(w.x + 1, w.y + 3, 1, 1); }   // someone watching
  }
  halo(HL.shop, 40, G - 7, 0.9);
  // wind runs through the scaffold's net: lighter folds travelling along it
  for (let k = 0; k < 5; k++) {
    const u = frac(t * 6 / LOOP + k / 5), x = Math.round(NET.x0 + u * (NET.x1 - NET.x0 + 12)) - 6, a = Math.sin(Math.PI * u);
    ctx.globalAlpha = 0.22 * a; ctx.fillStyle = '#9af0c8';
    for (let y = NET.y0; y < NET.y1 - 8; y += 2) ctx.fillRect(x + Math.round(2.5 * Math.sin(y * 0.11 + k)), y + ((x + k) & 1), 3, 1);
  }
  ctx.globalAlpha = 1;
  SITE_LAMPS.forEach((x, i) => {                                                   // warning lamps blink out of step
    const on = frac(t * 0.75 + i * 0.37) < 0.45;
    cput(x, G - 9, on ? '#ffd040' : '#6a4a10'); cput(x, G - 8, '#3a3a48');
    if (on) halo(HL.amber, x, G - 9, 0.9);
  });
}

/* ================= The banner on the scaffold: the city's own ad, a record shop ================= */
const BB_LAMPS = [BB.x + 11, BB.x + BB.w - 12];       // two floodlamps on arms above the banner
const BB_FLICK = [[63, 64.2], [170.5, 171.4]];         // the right one stutters now and then
function renderBanner() {                              // frame, ties to the standards and the lamp arms (no face)
  const p = painter(BB.w + 8, BB.h + 12), put = (i, y, col) => p.put(i - BB.x + 4, y - BB.y + 8, col);
  for (let i = -1; i <= BB.w; i++) { put(BB.x + i, BB.y - 1, '#e8e4dc'); put(BB.x + i, BB.y + BB.h, '#8a8690'); }
  for (let y = -1; y <= BB.h; y++) { put(BB.x - 1, BB.y + y, '#e8e4dc'); put(BB.x + BB.w, BB.y + y, '#8a8690'); }
  for (const [dx, dy] of [[-2, -2], [BB.w + 1, -2], [-2, BB.h + 1], [BB.w + 1, BB.h + 1]]) { put(BB.x + dx, BB.y + dy, '#c8c4bc'); put(BB.x + dx + (dx < 0 ? -1 : 1), BB.y + dy + (dy < 0 ? -1 : 1), '#c8c4bc'); }
  for (const lx of BB_LAMPS) { for (let y = BB.y - 7; y < BB.y - 1; y++) put(lx, y, '#50546a'); for (let i = -2; i <= 2; i++) put(lx + i, BB.y - 7, i === -2 ? '#8a8ea0' : '#2a2c3a'); }
  return p.done();
}
function renderAd() {                                  // KNISTER (crackle) VINYL: a record, the name, a little equaliser
  const p = painter(BB.w, BB.h);
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < BB.w; i++) p.put(i, y, ramp(['#123c40', '#0e3034', '#0a2428'], y / BB.h + Math.abs(i - 24) / 90, i, y, 1));
  for (let y = 0; y < BB.h; y++) for (let i = 0; i < 20; i++) {
    const d = Math.hypot(i - 9.5, y - 9.5);
    if (d < 8.3) p.put(i, y, d < 1 ? '#f4f0e0' : d < 3.2 ? (i + y < 19 ? '#ff9a3a' : '#d86a20') : (Math.round(d) & 1) ? '#16161e' : '#2a2a36');
  }
  const c = p.done();
  text(p.x, 'KNISTER', 20, 3, '#f4ecd8');
  text(p.x, 'VINYL', 20, 11, '#ff9a3a');
  return c;
}
function drawBanner(t, pitch) {
  halo(HL.bbWash, BB.x + (BB.w >> 1), BB.y + 8, 0.8);
  ctx.drawImage(BB_C, BB.x - 4, BB.y - 8);
  if (!PITCH.covers(t, pitch)) {
    ctx.drawImage(AD_C, BB.x, BB.y);
    const a = TAU * frac(t / 1.6);                                               // the record turns: a glint sweeps its grooves
    for (const r of [4.5, 6, 7.2]) { cput(BB.x + Math.round(9.5 + Math.cos(a) * r), BB.y + Math.round(9.5 + Math.sin(a) * r), '#8a8aa0'); cput(BB.x + Math.round(9.5 - Math.cos(a) * r), BB.y + Math.round(9.5 - Math.sin(a) * r), '#4a4a5c'); }
    const s = step(t + 0.11, 960);                                                // equaliser bars
    for (let k = 0; k < 5; k++) { const hgt = 1 + Math.floor(hash(k, s, 521) * 6); rect(ctx, BB.x + 41 + k, BB.y + 17 - hgt, 1, hgt, k & 1 ? '#a8f000' : '#d8ff60'); }
  }
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
  BB_LAMPS.forEach((lx, i) => {
    let li = 0.95 + 0.05 * wave(t, 1700, 0.25 + i * 0.5);
    if (i === 1 && inEvent(t, BB_FLICK)) { const hv = hash(19, Math.floor(t * 24)); li *= hv > 0.5 ? 1 : hv > 0.22 ? 0.4 : 0.1; }
    if (li < 0.9) { ctx.globalAlpha = (0.9 - li) * 0.5; rect(ctx, lx - 9, BB.y, 19, 9, '#06080c'); ctx.globalAlpha = 1; }
    ctx.fillStyle = li > 0.6 ? '#fff6d8' : li > 0.2 ? '#c8a070' : '#4a3a30';
    ctx.fillRect(lx - 1, BB.y - 6, 3, 1);
    halo(HL.bbLamp, lx, BB.y - 5, li);
  });
}

/* ================= Pariser Platz: granite slabs running to the gate, damp enough to mirror the show ================= */
const PAVE = ['#17172a', '#202036', '#2a2a44', '#353552', '#424260', '#52506c'];
const JOINT_ROWS = [];
for (let k = 0; k < 10; k++) JOINT_ROWS.push(Math.round(VP.y + 26 * Math.pow(1.19, k)));
const isJoint = (x, y) => {
  if (JOINT_ROWS.includes(y)) return true;
  const xb = VP.x + (x - VP.x) * (H - VP.y) / (y - VP.y);                          // where this ray meets the bottom row
  return ((xb + 2000) % 46) < (H - VP.y) / (y - VP.y);                             // a joint every 46 px down there, 1 px wide here
};
function paveCol(x, y) {
  const xb = VP.x + (x - VP.x) * (H - VP.y) / (y - VP.y), row = JOINT_ROWS.findIndex(j => y <= j), slab = Math.floor((xb + 2000) / 46);
  let v = 0.5 - 0.2 * (y - G) / (H - G) + (hash(slab, row, 601) - 0.5) * 0.14 + (hash(x, y, 602) - 0.5) * 0.06;
  v += Math.max(0, 1 - Math.abs(x - CX) / 170) * Math.max(0, 1 - (y - G) / 40) * 0.08;       // the gate's light on the ground
  v -= Math.pow(Math.abs(x - CX) / 300, 2) * 0.2;
  return ramp(PAVE, v, x, y, 0.9);
}
function renderGround() {
  const p = painter(W, H);
  for (let y = G + 1; y < H; y++) for (let x = 0; x < W; x++) p.put(x, y, isJoint(x, y) ? '#12121f' : paveCol(x, y));
  const r = rng(611), FALLEN = ['#b8922c', '#8e6c22', '#d0b038', '#7a5220'];              // fallen leaves, thicker under the lindens
  for (let n = 0; n < 520; n++) {
    const x = Math.floor(r() * W), y = G + 3 + Math.floor(r() * (H - G - 4)), edge = Math.max(0, 1 - Math.min(x, W - x) / 150), col = FALLEN[Math.floor(r() * 4)];
    if (r() > 0.22 + 0.78 * edge * edge) continue;
    p.put(x, y, mixHex(col, '#1a1a2c', 0.25)); if (y > 222 && r() < 0.6) p.put(x + 1, y, mixHex(col, '#1a1a2c', 0.4));
  }
  return p.done();
}
// laid over the mirror image: the joints, and more and more dry stone away from the gate
function renderVeil() {
  const p = painter(W, H);
  for (let y = G + 1; y < H; y++) for (let x = 0; x < W; x++) {
    if (isJoint(x, y)) p.put(x, y, '#12121f');
    else if (hash(x >> 1, y, 603) < 0.1 + 0.25 * smooth(G + 10, G + 50, y)) p.put(x, y, paveCol(x, y));          // dry patches
  }
  return p.done();
}
const MIRROR_BANDS = [[0.5, 25], [0.4, 25], [0.3, 25], [0.2, 25], [0.1, 9]];   // from the ground up: alpha, rows of the gate
const MIRROR = 0.44;                                  // how tall the gate's reflection is, as a share of the gate
function drawMirror() {                               // call right after drawGate (PJ_C holds this frame's lit front)
  ctx.setTransform(1, 0, 0, -MIRROR, 0, (G + 1) * (1 + MIRROR));
  let sy = PJ.h;
  for (const [a, rows] of MIRROR_BANDS) {                                          // fainter away from the gate
    sy -= rows; ctx.globalAlpha = a;
    ctx.drawImage(PJ_C, 0, sy, PJ.w, rows, PJ.x, PJ.y + sy, PJ.w, rows);
  }
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.globalAlpha = 1;
  ctx.drawImage(VEIL_C, 0, 0);
}
// flattened dithered ellipse of light on the ground (drawn with alpha = the lamp's level)
function makePool(rx, ry, col, maxA) {
  const p = painter(rx * 2 + 1, ry * 2 + 1);
  for (let y = 0; y <= ry * 2; y++) for (let i = 0; i <= rx * 2; i++) {
    const d = Math.hypot((i - rx) / rx, (y - ry) / ry);
    if (d >= 1) continue;
    const q = Math.floor(Math.pow(1 - d, 1.4) * 5 + bayer(i, y)) / 5;
    if (q > 0) p.put(i, y, col, Math.round(Math.min(1, q) * maxA * 255));
  }
  return p.done();
}

/* ----- the square's lamps: cast-iron posts with a lantern, dimmed for the countdown and the finale ----- */
const LAMPS = [{ x: 116, y: 201, h: 34 }, { x: 363, y: 201, h: 34 }, { x: 50, y: 249, h: 72 }, { x: 434, y: 247, h: 70 }];
const lampLevel = t => 1 - 0.85 * smooth(DARK.a, DARK.a + 4, t) * (1 - smooth(FINALE.b - 1, FINALE.b + 3, t));
function renderLamp(L) {
  const big = L.h > 50, w = big ? 11 : 7, p = painter(w, L.h), c = w >> 1, head = big ? 11 : 7;
  for (let y = head; y < L.h; y++) {
    const foot = y > L.h - (big ? 9 : 5), wide = foot ? (big ? 2 : 1) : 0;
    for (let i = -wide - (big ? 1 : 0); i <= wide; i++) p.put(c + i, y, i < 0 || (!big && !foot) ? '#3e4254' : '#14141e');
    if (!big) p.put(c, y, '#2a2c3a');
  }
  if (big) for (const y of [head + 6, L.h - 10]) for (let i = -2; i <= 2; i++) p.put(c + i, y, i < 0 ? '#565a70' : '#1a1a26');   // collars
  // the lantern: a dark frame, glass lit from inside (its glow is drawn per frame), a little roof
  for (let y = 0; y < head; y++) for (let i = -(w >> 1); i <= (w >> 1); i++) {
    const roof = y < (big ? 3 : 2), hw = roof ? (big ? 1 + y : y + 1) : (w >> 1) - (y > head - 3 ? 1 : 0);
    if (Math.abs(i) > hw) continue;
    p.put(c + i, y, roof || y === head - 1 || Math.abs(i) === hw ? '#16161f' : '#5a4a30');
  }
  return p.done();
}
function drawLamp(L, t) {
  const big = L.h > 50, w = big ? 11 : 7, top = L.y - L.h + 1, lv = lampLevel(t) * (0.95 + 0.05 * wave(t, 600 + L.x, 0.2));
  ctx.drawImage(L.c, L.x - (w >> 1), top);
  const gx = L.x - (big ? 3 : 1), gy = top + (big ? 3 : 2), gw = big ? 7 : 3, gh = big ? 6 : 3;
  ctx.fillStyle = lv > 0.6 ? '#fff0c0' : lv > 0.3 ? '#d8a860' : '#7a5a38'; ctx.fillRect(gx, gy, gw, gh);
  ctx.fillStyle = lv > 0.6 ? '#ffd080' : '#8a6238'; ctx.fillRect(gx, gy + gh - (big ? 2 : 1), gw, big ? 2 : 1);
  if (big) { ctx.fillStyle = '#16161f'; ctx.fillRect(L.x, gy, 1, gh); }
  halo(big ? HL.lampBig : HL.lamp, L.x, gy + 2, lv); halo(HL.lampCore, L.x, gy + 2, lv);
}
function drawPools(t) {
  const lv = lampLevel(t);
  for (const L of LAMPS) { ctx.globalAlpha = lv; ctx.drawImage(L.h > 50 ? POOL.big : POOL.small, L.x - (L.h > 50 ? 44 : 24), L.y - (L.h > 50 ? 9 : 5)); }
  ctx.globalAlpha = 0.85;
  for (const x of PASS_LAMP) ctx.drawImage(POOL.pass, x - 12, G - 2);              // warm light spilling out of the passages
  ctx.globalAlpha = 1;
}

/* ================= People: drawn from a few numbers, at any height from 9 to 32 px ================= */
// figure(h, style, pose, frame, flip) paints one person h px tall into a small canvas. Poses: 'walk' (side view, four
// frames), 'side' (standing side view), 'sideUp' (an arm raised), 'back' (standing, back to us, frame 1 leans),
// 'phone' (both arms up holding a phone), 'cheer' (arms up in a V), 'point' (an arm out). The cell is wider and taller
// than the body (FIG_PAD) so raised arms and strides fit; the feet stand on its bottom row, the body's axis on cx.
const NIGHT = c => mixHex(c, '#0a0c22', 0.18);
const COATS = ['#2a3046', '#56282e', '#3a4a3a', '#7a6648', '#c8a878', '#22222c', '#8a322a', '#2e5262', '#8a8a94', '#d8d0c0',
  '#e0a838', '#5a3a6e', '#c84a3a', '#2a6e4e', '#3a5a9a', '#b86a8a'].map(NIGHT);
const LEGS = ['#1a1a26', '#2a3050', '#3a3028', '#12121a', '#4a4a58'].map(NIGHT);
const SKINS = ['#f0c8a0', '#e0a880', '#c08860', '#8a5a3c', '#f4d4b4'];
const HAIRS = ['#2a1a12', '#5a3a1e', '#c8a050', '#1a1418', '#8a8890', '#a84a20'];
const HATCOLS = ['#c8303a', '#e8c040', '#2a6a8a', '#e8e4dc', '#3a3a44', '#a8f000', '#ff7ab0', '#3a6a3a'];
const SCARVES = ['#c8303a', '#e8c040', '#e8e4dc', '#3a8a8a', '#ff8a3a', '#7a4ac0', '#a8f000'];
// hat: 0 none, 1 beanie, 2 bobble hat, 3 cap, 4 hood; bag: 0 none, 1 backpack, 2 tote
function makeStyle(seed, over = {}) {
  const r = rng(seed * 7919 + 13), pick = a => a[Math.floor(r() * a.length)], hv = r(), bv = r();
  return { coat: pick(COATS), legs: pick(LEGS), skin: pick(SKINS), hair: pick(HAIRS), hat: hv < 0.34 ? 0 : hv < 0.6 ? 1 : hv < 0.72 ? 2 : hv < 0.85 ? 3 : 4,
    hatCol: pick(HATCOLS), scarf: r() < 0.6 ? pick(SCARVES) : null, bag: bv < 0.5 ? 0 : bv < 0.8 ? 1 : 2, bagCol: NIGHT(pick(HATCOLS)), long: r() < 0.4, kid: false, ...over };
}
const FIG_PAD = h => Math.ceil(h * 0.34) + 2;
function figure(h, st, pose, f = 0, flip = false) {
  const pad = FIG_PAD(h), cw = pad * 2 + 1, mt = Math.ceil(h * 0.32), p = painter(cw, h + mt), cx = pad;
  const put = (i, y, col) => { if (col) p.put(flip ? cw - 1 - i : i, y + mt, col); };
  const hd = clamp(Math.round(h * (st.kid ? 0.24 : 0.17)), 2, 5), neck = hd, hip = Math.round(h * (st.long ? 0.68 : 0.57));
  const lt = h < 15 ? 1 : h < 26 ? 2 : 3, side = pose === 'walk' || pose === 'side' || pose === 'sideUp';
  const cd = darker(st.coat, 0.3), cl = lighter(st.coat, 0.16), ld = darker(st.legs, 0.3), shoe = '#0c0c14';
  const leg = (x0, dx, col, lift = 0) => {
    for (let k = 0; k < lt; k++) pline(put, x0 + k, hip, x0 + k + dx, h - 1 - lift, col);
    for (let k = 0; k < lt; k++) put(x0 + k + dx, h - 1 - lift, shoe);
    if (h >= 14 && side) put(x0 + lt + dx, h - 1 - lift, shoe);                     // the toe
  };
  if (side) {
    const tw = clamp(Math.round(h * 0.2), 2, 6), x0 = cx - (tw >> 1), stride = Math.max(1, Math.round(h * 0.14));
    const lx = cx - (lt >> 1), wf = pose === 'walk' ? f : 1;
    const nearDx = [stride, 0, -stride, 0][wf], farDx = -nearDx;
    leg(lx, farDx, ld, pose === 'walk' && (wf & 1) ? 1 : 0);
    leg(lx, nearDx, st.legs);
    if (st.bag === 1) { const pw = Math.max(1, Math.round(h * 0.1)); for (let y = neck + 1; y <= neck + 1 + Math.round(h * 0.22); y++) for (let i = 1; i <= pw; i++) put(x0 - i, y, y === neck + 1 ? lighter(st.bagCol, 0.25) : st.bagCol); }
    for (let y = neck; y < hip + (st.long ? 0 : 1); y++) for (let i = 0; i < tw; i++)
      put(x0 + i, y, y === neck && st.scarf ? st.scarf : i === 0 ? cd : i === tw - 1 ? cl : st.coat);
    if (st.scarf && h >= 14) put(x0 - 1, neck + 1, st.scarf);
    // the near arm: swinging against the legs, or raised
    const sy = neck + 1, hand = hip - 1;
    if (pose === 'sideUp') { const ex = cx + Math.round(h * 0.2), ey = neck - Math.round(h * 0.12); pline(put, cx, sy, ex, ey, cd); put(ex, ey, st.skin); }
    else { const ax = cx + (pose === 'walk' ? -Math.round(nearDx * 0.7) : 0); pline(put, cx, sy, ax, hand, cd); if (h >= 14) put(ax, hand, st.skin); if (st.bag === 2) for (let j = 1; j <= Math.max(2, Math.round(h * 0.12)); j++) for (let i = -1; i <= (h > 18 ? 1 : 0); i++) put(ax + i, hand + j, st.bagCol); }
    // head, facing right
    const hx = cx - (hd >> 1) + (h >= 18 ? 1 : 0);
    for (let y = 0; y < hd; y++) for (let i = 0; i < hd; i++) {
      let col = (y === 0 || i === 0) ? st.hair : st.skin;
      if (st.hat === 4) col = i === hd - 1 && y > 0 ? st.skin : cl;
      else if (st.hat && y === 0) col = st.hatCol;
      else if ((st.hat === 1 || st.hat === 2) && y === 1 && hd >= 4) col = i === hd - 1 ? st.skin : st.hatCol;
      if (hd >= 4 && i === hd - 2 && y === 2 && st.hat !== 4) col = '#2a1a1a';        // an eye
      put(hx + i, y, col);
    }
    if (st.hat === 2) put(hx + (hd >> 1), -1, '#f4f0e8');
    if (st.hat === 3) put(hx + hd, hd > 2 ? 1 : 0, st.hatCol);                     // the cap's peak
  } else {
    const tw = clamp(Math.round(h * 0.3), 3, 9), lean = pose === 'back' && f === 1 ? 1 : 0, x0 = cx - (tw >> 1) + lean;
    const gap = tw >= 5 ? 1 : 0, span = lt * 2 + gap, lx = cx - (span >> 1);
    for (let y = hip; y < h; y++) for (let k = 0; k < lt; k++) {
      put(lx + k, y, y === h - 1 ? shoe : k === 0 ? st.legs : ld); put(lx + lt + gap + k, y, y === h - 1 ? shoe : ld);
    }
    const armsUp = pose === 'phone' || pose === 'cheer';
    for (let y = neck; y < hip + (st.long ? 0 : 1); y++) for (let i = 0; i < tw; i++) {
      if (y === neck && (i === 0 || i === tw - 1) && tw > 3) continue;                // rounded shoulders
      const arm = (i === 0 || i === tw - 1) && y > neck && y < hip - 1 && tw > 3;
      put(x0 + i, y, y === neck ? (st.scarf || cl) : arm && !(armsUp || (pose === 'point' && i === tw - 1)) ? cd : y === neck + 1 ? cl : st.coat);
    }
    if (st.bag === 1 && tw > 3) for (let y = neck + 2; y <= neck + 2 + Math.round(h * 0.2); y++) for (let i = 1; i < tw - 1; i++) put(x0 + i, y, y === neck + 2 ? lighter(st.bagCol, 0.25) : st.bagCol);
    const hx = cx - (hd >> 1) + lean;
    for (let y = 0; y < hd; y++) for (let i = 0; i < hd; i++) {
      let col = st.hair;
      if (st.hat === 4) col = i === 0 ? cl : st.coat;
      else if (st.hat === 3) col = y === 0 ? st.hatCol : st.hair;
      else if (st.hat) col = y < hd - 1 || hd < 3 ? st.hatCol : st.hair;
      put(hx + i, y, col);
    }
    if (st.hat === 2) put(hx + (hd >> 1), -1, '#f4f0e8');
    if (st.long && st.hat === 0 && hd >= 3) for (let i = 0; i < hd; i++) put(hx + i, hd, st.hair);   // long hair over the collar
    const shL = x0, shR = x0 + tw - 1, up = Math.round(h * 0.14);
    if (pose === 'phone') {
      const pw = Math.max(2, hd - 1), ph = Math.max(2, Math.round(h * 0.11)), px0 = cx - (pw >> 1), py = -up - ph + 1;
      pline(put, shL, neck + 1, px0 - 1, py + ph - 1, cd); pline(put, shR, neck + 1, px0 + pw, py + ph - 1, cd);
      put(px0 - 1, py + ph - 1, st.skin); put(px0 + pw, py + ph - 1, st.skin);
      for (let y = 0; y < ph; y++) for (let i = 0; i < pw; i++) put(px0 + i, py + y, y === ph - 1 && ph > 2 ? '#3a4a6a' : '#a8d8ff');   // the screen (tinted per frame)
    } else if (pose === 'cheer') {
      const ex = Math.round(h * 0.16);
      pline(put, shL, neck + 1, shL - ex, -up, cd); pline(put, shR, neck + 1, shR + ex, -up, cd);
      put(shL - ex, -up, st.skin); put(shR + ex, -up, st.skin);
    } else if (pose === 'point') {
      const ex = shR + Math.round(h * 0.26), ey = neck - Math.round(h * 0.1);
      pline(put, shR, neck + 1, ex, ey, cd); put(ex, ey, st.skin);
    }
  }
  return { c: p.done(), cx, by: h + mt - 1, h };
}
// draw a figure standing with its feet at (x, y)
const drawFig = (F, x, y) => ctx.drawImage(F.c, Math.round(x) - F.cx, Math.round(y) - F.by);
const FIG_CACHE = new Map();
function fig(h, seed, pose, f = 0, flip = false, over) {
  const k = `${h}|${seed}|${pose}|${f}|${flip ? 1 : 0}`;
  let F = FIG_CACHE.get(k);
  if (!F) { F = figure(h, makeStyle(seed, over), pose, f, flip); FIG_CACHE.set(k, F); }
  return F;
}

/* ================= The crowd on the square ================= */
const BAND = { pre: 46, a: 58, b: 110 };              // the brass band: heads turn from 46 s, it marches through 58-110 s
// ----- spectators, backs to us, watching the show: [x, feet row]; the nearest stand tall at the bottom edge -----
const WATCH_AT = [
  [140, 192], [146, 191], [176, 191], [181, 192], [203, 190], [236, 191], [242, 192], [247, 190], [270, 191], [296, 192], [301, 190], [338, 191], [344, 193],
  [92, 205], [101, 203], [110, 207], [124, 204], [150, 202], [160, 206], [232, 203], [243, 205], [254, 202], [266, 207], [278, 204],
  [330, 204], [340, 207], [352, 203], [384, 206], [396, 203], [410, 207],
  [258, 219], [269, 224], [281, 220], [34, 228], [22, 223], [469, 234], [424, 221],
  [26, 266, 187], [262, 268, 191], [278, 267, 196], [468, 265, 184],
];
// every spectator thins with the crowd, and takes their phone, its flash and their pointing and cheering along
const WATCHERS = WATCH_AT.map(([x, y, seed], i) => ({ x, y, i, seed: seed ?? 100 + i, n: 4 + (i * 7) % 5, ph: hash(i, 1, 701), lag: hash(i, 2, 701) * 8,
  phone: hash(i, 3, 701) < 0.75, flash: hash(i, 4, 701) < 0.5, rank: rankOf(i) }));
function buildWatchers() {
  for (const w of WATCHERS) {
    const h = tall(w.y);
    w.h = h;
    w.F = [fig(h, w.seed, 'back', 0), fig(h, w.seed, 'back', 1), fig(h, w.seed, 'phone'), fig(h, w.seed, 'cheer'), fig(h, w.seed, 'point'), fig(h, w.seed, 'point', 0, true),
      fig(h, w.seed, 'side', 0), fig(h, w.seed, 'side', 0, true)];
    w.py = w.y - h - Math.round(h * 0.14) - 1;                                    // where the raised phone is
  }
}
// what a spectator is doing at t: 0/1 standing (shifting their weight), 2 phone up, 3 cheering, 4/5 pointing right/left,
// 6/7 turned to a neighbour
function watcherPose(w, t) {
  const i = w.i;
  if (t >= FINALE.a && t < FINALE.b + 2) return finaleFlash(t) > 0.25 && i % 3 === 0 ? 3 : i % 5 !== 4 ? 2 : (step(t, 480) + i) & 1;
  if (t >= DARK.a + 3 + w.lag && t < FINALE.a) return 2;                          // the phones come up during the countdown
  const b0 = BAND.pre + w.lag * 1.2;
  if (w.x < 300 && w.y > 198 && t >= b0 && t < b0 + 4.5) return 5;                // "listen, a band!"
  const f0 = FAN.a + 2 + w.lag * 1.3;
  if (i % 2 === 0 && t >= f0 && t < f0 + 4) return w.x < CX ? 4 : 5;              // pointing at the beams coming on
  const u = frac(t * w.n / LOOP + w.ph);
  if (w.phone && u > 0.12 && u < 0.36) return 2;
  const hv = hash(i, step(t + w.ph * 9, w.y > 255 ? 60 : 96), 702);              // every 2.5 s (the nearest: 4 s)
  if (w.y > 255) return hv < 0.25 ? 0 : hv < 0.45 ? 1 : hv < 0.62 ? 6 : hv < 0.8 ? 7 : hv < 0.92 ? 2 : 4;
  return hv < 0.36 ? 0 : hv < 0.72 ? 1 : hv < 0.86 ? 6 : 7;
}
function drawWatcher(w, t) {
  const pose = watcherPose(w, t);
  drawFig(w.F[pose], w.x, w.y);
  if (pose !== 2) return;
  const big = w.h > 14, fin = t >= FINALE.a && t < FINALE.b;
  ctx.fillStyle = ACT_COL[actAt(t).a]; ctx.fillRect(w.x - (big ? 1 : 0), w.py, 1, 1);     // the show on the little screen
  const sec = frac(t * w.n / LOOP + w.ph) * LOOP / w.n;                           // seconds into this spectator's own cycle
  const fl = fin ? hash(w.i, Math.floor(t * 5), 703) < 0.1 : w.flash && sec > LOOP / w.n * 0.2 && sec < LOOP / w.n * 0.2 + 0.16;
  if (fl) { ctx.fillStyle = '#ffffff'; ctx.fillRect(w.x - 1, w.py - 1, big ? 3 : 2, big ? 3 : 2); halo(big ? HL.flash : HL.flashS, w.x, w.py, 1); }
}

// ----- the stream of walkers: [feet row, crossings per loop, phase, direction, seed, companion] -----
// companion: 'pair' (two side by side), 'kid' (a child by the hand), 'dog' (on a lead), 'pram'
const WALK_DEFS = [
  [194, 3, 0.08, 1, 201], [194, 3, 0.52, -1, 202], [195, 3, 0.31, 1, 203, 'pair'], [196, 3, 0.77, -1, 204], [196, 4, 0.93, 1, 205], [195, 3, 0.64, -1, 206],
  [198, 4, 0.05, 1, 211, 'pair'], [199, 4, 0.55, -1, 212], [200, 4, 0.3, -1, 213, 'kid'], [209, 5, 0.15, 1, 214], [210, 5, 0.7, -1, 215, 'pair'],
  [215, 5, 0.42, 1, 216, 'kid'], [216, 6, 0.9, -1, 217],
  [240, 7, 0.1, -1, 221, 'pair'], [241, 7, 0.62, 1, 222], [247, 8, 0.35, 1, 223, 'dog'], [248, 8, 0.8, -1, 224, 'pram'], [257, 9, 0.5, -1, 225], [258, 9, 0.02, 1, 226, 'pair'],
];
// one rank per group: a couple, a parent and child, an owner and dog, a pram and its pusher come and go together
const WALKERS = WALK_DEFS.map(([y, n, ph, dir, seed, mate], i) => ({ y, n, ph, dir, seed, mate, h: tall(y), m: 30, rank: rankOf(i, 0.5) }));
const DOG_ROWS = [['..........ee.', '.........dddd', 't........dddn', '.tdddddddddw.', '..ddddddddd..', '..dd.....dd..', '..d.d...d.d..', '.d...d.d...d.'],
  ['..........ee.', '.........dddd', '.t.......dddn', '.tdddddddddw.', '..ddddddddd..', '..dd.....dd..', '...dd....dd..', '...d.d...d.d.']];
const DOG_KEY = { d: '#b08050', e: '#6a4426', n: '#1a1418', t: '#b08050', w: '#e8dcc8' };
let DOG_SPR, PRAM_SPR;
function buildWalkers() {
  for (const w of WALKERS) {
    const fl = w.dir < 0;
    w.F = [0, 1, 2, 3].map(f => fig(w.h, w.seed, 'walk', f, fl));
    w.step = Math.max(2, Math.round(w.h * 0.3));
    if (w.mate === 'pair') w.G = [0, 1, 2, 3].map(f => fig(w.h - 1, w.seed + 50, 'walk', f, fl));
    if (w.mate === 'kid') { w.kh = Math.round(w.h * 0.62); w.G = [0, 1, 2, 3].map(f => fig(w.kh, w.seed + 50, 'walk', f, fl, { kid: true, bag: 0, long: false })); }
  }
  DOG_SPR = DOG_ROWS.map(rows => [sprite(rows, DOG_KEY), sprite(rows, DOG_KEY, true)]);
  PRAM_SPR = [false, true].map(fl => sprite(['.hhhhhh....', 'hhhhhhhh...', 'hhHHHHhh..k', 'bbbbbbbbbk.', 'bbbbbbbbk..', '.bbbbbbb...', '..k...k....', '.kwk.kwk...', '..k...k....'],
    { h: '#3a5a8a', H: '#5a7ab0', b: '#2a3a5a', k: '#16161e', w: '#8a8a94' }, fl));
}
function drawWalker(w, t) {
  const span = W + 2 * w.m, d = frac(t * w.n / LOOP + w.ph) * span, x = w.dir > 0 ? -w.m + d : W + w.m - d;
  const f = Math.floor(d / w.step) & 3;
  if (w.mate === 'pair') drawFig(w.G[(f + 2) & 3], x - w.dir * Math.round(w.h * 0.42), w.y - 1 - ((f + 2) & 1));
  drawFig(w.F[f], x, w.y - (f & 1));
  if (w.mate === 'kid') {
    const kx = x + w.dir * Math.round(w.h * 0.4);
    drawFig(w.G[(f + 1) & 3], kx, w.y - ((f + 1) & 1));
    line(Math.round(x + w.dir * 2), w.y - Math.round(w.h * 0.45), Math.round(kx - w.dir), w.y - Math.round(w.kh * 0.62), '#c89870');   // hand in hand
  } else if (w.mate === 'dog') {
    const dx = Math.round(x + w.dir * (w.h * 0.75)), spr = DOG_SPR[f & 1][w.dir > 0 ? 0 : 1];
    ctx.drawImage(spr, dx - 6, w.y - 7);
    line(Math.round(x + w.dir * 2), w.y - Math.round(w.h * 0.42), dx + w.dir * 3, w.y - 5, '#c8303a');
  } else if (w.mate === 'pram') {
    ctx.drawImage(PRAM_SPR[w.dir > 0 ? 0 : 1], Math.round(x + w.dir * (w.h * 0.45)) - (w.dir > 0 ? 0 : 10), w.y - 8);
  }
}

/* ================= The busker, his listeners, the dancing couple, the photographers ================= */
const BEAT = 0.75;                                    // 80 bpm: 320 beats per loop
const beatOf = t => Math.floor(t / BEAT);
const APPLAUSE = [[36, 40], [96, 100], [156, 160], [228, 232]];   // a song ends: he bows, they clap
const COINS = [41, 161];                              // a listener steps up and drops a coin in the case (7 s)
const BUSK = { x: 172, y: 223 };
const LISTEN_AT = [[147, 221, 1], [153, 227, 1, 1], [158, 216, 1], [150, 234, 1], [199, 218, -1], [206, 224, -1], [212, 230, -1, 1], [200, 237, -1, 0, 1]];   // x, y, facing, kid, coin giver
// the coin giver always stays (rank 0); each child shares the rank of the adult beside them; the ones in front of him go last
const LISTEN_RANK = [0.65, 0.45, 0.85, 0.45, 0.75, 0.15, 0.15, 0];
const LISTENERS = LISTEN_AT.map(([x, y, face, kid, coin], i) => ({ x, y, face, kid, coin, i, seed: 310 + i, rank: LISTEN_RANK[i] }));
let BUSK_F, CASE_C, NOTE_C;
function buildBusker() {
  const h = tall(BUSK.y);
  BUSK_F = fig(h, 300, 'side', 0, false, { hat: 3, hatCol: '#2a2a34', coat: NIGHT('#7a4a2a'), scarf: '#c8303a', bag: 0, long: false, hair: '#5a3a1e' });
  CASE_C = sprite(['.kkkkkkkkkkkkkkk.', 'krrrrrrrrrrrrrrrk', 'krrrrrrrrrrrrrrrk', 'kkkkkkkkkkkkkkkkk', '.KKKKKKKKKKKKKKK.'], { k: '#1a1418', K: '#0c0a10', r: '#7a1a26' });
  NOTE_C = ['#a8f000', '#ffffff', '#ffd23a'].map(c => sprite(['.##', '.#.', '.#.', '##.', '##.'], { '#': c }));
  for (const L of LISTENERS) {
    const h2 = Math.round(tall(L.y) * (L.kid ? 0.64 : 1)), over = L.kid ? { kid: true, long: false, bag: 0 } : undefined, fl = L.face < 0;
    L.h = h2;
    L.F = L.face ? [fig(h2, L.seed, 'side', 0, fl, over), fig(h2, L.seed, 'sideUp', 0, fl, over)] : [fig(h2, L.seed, 'back', 0, false, over), fig(h2, L.seed, 'cheer', 0, false, over)];
    if (L.coin) L.W = [false, true].map(f => [0, 1, 2, 3].map(k => fig(h2, L.seed, 'walk', k, f, over)));
  }
}
function drawBusker(t) {
  const { x, y } = BUSK, bow = inEvent(t, APPLAUSE), b = beatOf(t), dy = bow ? 2 : (b & 1);
  drawFig(BUSK_F, x, y + dy);
  const gy = y - 10 + dy;                                                        // the guitar: body, sound hole, neck, his strumming hand
  ctx.fillStyle = '#c8843a'; ctx.fillRect(x - 1, gy, 6, 4); ctx.fillRect(x, gy - 1, 4, 6);
  cput(x + 2, gy + 1, '#3a2010'); ctx.fillStyle = '#e8a85a'; ctx.fillRect(x, gy - 1, 3, 1);
  line(x + 5, gy, x + 11, gy - 4, '#6a4426'); cput(x + 12, gy - 5, '#d8c8a0');
  if (!bow) cput(x + 1, gy + (step(t, 960) & 1 ? 1 : 3), '#f0c8a0');
  ctx.drawImage(CASE_C, x + 10, y - 2);                                          // the open case, coins catching the light
  for (let k = 0; k < 4; k++) cput(x + 13 + k * 3 + (k & 1), y - 1 + (k & 1), hash(k, step(t, 480), 711) < 0.3 ? '#fff6c0' : '#c8a040');
  if (bow) return;
  for (let k = 0; k < 4; k++) {                                                  // notes drifting up from the strings
    const { cyc, u } = cycle(t, 48, k / 4);
    ctx.globalAlpha = Math.sin(Math.PI * u) * 0.95;
    ctx.drawImage(NOTE_C[(k + cyc) % 3], Math.round(x + 9 + k * 5 + 4 * Math.sin(u * 5 + k * 2)), Math.round(y - 24 - u * 22));
  }
  ctx.globalAlpha = 1;
}
function drawListener(L, t) {
  if (L.coin) for (const c0 of COINS) {                                          // walks to the case, bends, walks back
    const s = t - c0;
    if (s < 0 || s >= 7) continue;
    const go = s < 2.4, back = s >= 4.6, u = go ? s / 2.4 : back ? 1 - (s - 4.6) / 2.4 : 1;
    const x = lerp(L.x, BUSK.x + 17, u), y = lerp(L.y, BUSK.y + 5, u);
    if (go || back) drawFig(L.W[(BUSK.x + 17 < L.x) === go ? 1 : 0][Math.floor(u * 9) & 3], x, y);
    else { drawFig(L.F[0], x, y + 2); cput(Math.round(x) + 2, Math.round(y) - 6 + Math.round((s - 2.4) * 3) % 5, '#ffe070'); }
    return;
  }
  const clap = inEvent(t, APPLAUSE), b = beatOf(t);
  const up = clap ? (step(t, 1440) + L.i) & 1 : 0, sway = !clap && hash(L.i, b >> 1, 712) < 0.5 ? (b & 1) : 0;
  drawFig(L.F[up], L.x, L.y - sway);
}
// two people dancing to him: a step on every beat, a turn that swaps their places every eight
const DANCE = { x: 233, y: 232 };
let DANCE_F;
function buildDance() {
  const h = tall(DANCE.y);
  DANCE_F = [321, 322].map((seed, k) => [false, true].map(fl => ['side', 'walk'].map(pose => fig(h - k, seed, pose, 0, fl, { bag: 0 }))));
}
function drawDance(t) {
  const b = t / BEAT, bi = Math.floor(b), q = b - 8 * Math.floor(b / 8), o = Math.floor(bi / 8) & 1 ? 1 : -1;
  const s = lerp(-o, o, smooth(0, 1.6, q)), d = 5 + (bi & 1), xa = DANCE.x - s * d, xb = DANCE.x + s * d, st = bi & 1, { y } = DANCE;
  const A = DANCE_F[0][xa > xb ? 1 : 0][st], B = DANCE_F[1][xb > xa ? 1 : 0][st ^ 1];
  drawFig(B, xb, y - 1 - (st ^ 1)); drawFig(A, xa, y - st);
  if (Math.abs(xa - xb) > 3) line(Math.round(xa), y - 14 - st, Math.round(xb), y - 14, '#e0b088');   // hand in hand
}
// photographers with tripods, long exposures of the gate; their flashes go off now and then
const PHOTOGS = [{ x: 304, y: 215, seed: 331, per: 8, ph: 0.1, rank: 0.2 }, { x: 132, y: 213, seed: 332, per: 8, ph: 0.55, rank: 0.55 },
  { x: 222, y: 209, seed: 333, per: 12, ph: 0.3, rank: 0.85 }];   // rank: each thins with the crowd, tripod and flash included
function buildPhotogs() { for (const P of PHOTOGS) { P.h = tall(P.y); P.F = [0, 1].map(f => fig(P.h, P.seed, 'back', f, false, { bag: 1 })); } }
function drawPhotog(P, t) {
  const u = frac(t / P.per + P.ph), bend = u < 0.3 ? 1 : 0, tx = P.x + Math.round(P.h * 0.5), ty = P.y - Math.round(P.h * 0.72);
  line(tx, ty, tx - 3, P.y, '#22222c'); line(tx, ty, tx + 3, P.y, '#22222c'); line(tx, ty, tx, P.y, '#3a3a48');
  ctx.fillStyle = '#16161e'; ctx.fillRect(tx - 2, ty - 3, 5, 3); cput(tx - 1, ty - 4, '#16161e');
  cput(tx + 1, ty - 2, hash(P.seed, step(t, 240), 713) < 0.5 ? '#ff3030' : '#3a1010');   // the recording light
  drawFig(P.F[bend], P.x, P.y + bend);
  if (u > 0.18 && u < 0.18 + 0.18 / P.per) { ctx.fillStyle = '#ffffff'; ctx.fillRect(tx - 2, ty - 5, 4, 3); halo(HL.flash, tx, ty - 4, 1); }
}
/* ----- steam ----- */
const STEAM = [];
{ const r = rng(641); for (let i = 0; i < 14; i++) STEAM.push({ n: 40 + Math.floor(r() * 14), ph: r(), k: 20 + Math.floor(r() * 20), p: r(), s: 0.6 + r() * 0.8 }); }
function puffs(list, count, t, x0, y0, rise, drift, rMax, alpha) {
  for (let i = 0; i < count; i++) {
    const s = list[i], q = t * s.n / LOOP + s.ph, u = q - Math.floor(q);
    const x = x0 + (0.35 * u + 0.65 * u * u) * drift * s.s + wave(t, s.k, s.p) * 2 * u, y = y0 - u * rise + u * u * rise * 0.25;
    const rr = Math.max(1, Math.min(rMax, 1 + Math.floor(u * (rMax + 1))));
    ctx.globalAlpha = alpha * Math.min(1, u * 14) * Math.pow(1 - u, 1.2);
    ctx.drawImage(PUFF[rr], Math.round(x) - rr, Math.round(y) - rr);
  }
  ctx.globalAlpha = 1;
}

/* ================= The soap-bubble artist and the children, the pretzel seller's bike, the hot-drink cart ================= */
const BUB = { x: 316, y: 233, n: 12, count: 8 };      // a sweep of the wand every 20 s, eight big bubbles each
let BUB_F, RING, KID_F;
const RING_COLS = [['#7af0ff', '#ff8ae0', '#d8ff70'], ['#ffb0f0', '#9ad0ff', '#fff0a0'], ['#b0ffd0', '#c8a0ff', '#ffffff'], ['#ffe080', '#70e8ff', '#ffa0c0']];
function makeRing(r, cols) {                          // a soap film: thin iridescent rim, a highlight
  const s = r * 2 + 1, p = painter(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) {
    const d = Math.hypot(i - r, y - r);
    if (Math.abs(d - r + 0.4) < 0.55) p.put(i, y, cols[(Math.floor(Math.atan2(y - r, i - r) / TAU * 6 + 6)) % 3], 235);
    else if (d < r - 0.4) p.put(i, y, cols[0], 26);
  }
  if (r > 2) { p.put(r - Math.round(r * 0.45), r - Math.round(r * 0.45), '#ffffff'); if (r > 4) p.put(r - Math.round(r * 0.45) + 1, r - Math.round(r * 0.45) - 1, '#ffffff'); }
  return p.done();
}
function buildBubbles() {
  const h = tall(BUB.y), over = { coat: NIGHT('#b83a6a'), hat: 1, hatCol: '#e8c040', bag: 0, long: true, scarf: '#5ce1e6' };
  BUB_F = [fig(h, 340, 'side', 0, false, over), fig(h, 340, 'sideUp', 0, false, over)];
  RING = RING_COLS.map(cols => [0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(r => (r < 2 ? null : makeRing(r, cols))));
  KID_F = [341, 342, 343].map(seed => [false, true].map(fl => ({ w: [0, 1, 2, 3].map(f => fig(13, seed, 'walk', f, fl, { kid: true, bag: 0, long: false })), up: fig(13, seed, 'sideUp', 0, fl, { kid: true, bag: 0, long: false }) })));
}
function wandTip(u) {                                  // the wand's tip through one sweep (relative to the artist's feet)
  if (u < 0.07) return [9, -4];                                                   // dipped in the bucket
  if (u < 0.14) { const s = (u - 0.07) / 0.07; return [lerp(9, 13, s), lerp(-4, -24, s)]; }
  if (u < 0.42) { const s = (u - 0.14) / 0.28, a = lerp(0.5, 2.3, s); return [4 + Math.cos(a) * 11, -19 - Math.sin(a) * 13]; }   // a slow arc overhead
  if (u < 0.5) { const s = (u - 0.42) / 0.08; return [lerp(-3.3, 11, s), lerp(-28.7, -17, s)]; }
  return [11, -17 + (u > 0.93 ? (u - 0.93) / 0.07 * 13 : 0)];
}
function drawBubbleArtist(t) {
  const { cyc, u } = cycle(t, BUB.n, 0.02), { x, y } = BUB, [wx, wy] = wandTip(u), up = u > 0.07 && u < 0.5;
  ctx.fillStyle = '#3a6a9a'; ctx.fillRect(x + 7, y - 4, 6, 5); ctx.fillStyle = '#9ad0ff'; ctx.fillRect(x + 8, y - 4, 4, 1);   // the bucket
  drawFig(BUB_F[up ? 1 : 0], x, y);
  const hx = x + (up ? 5 : 2), hy = y - (up ? 18 : 11), tx = Math.round(x + wx), ty = Math.round(y + wy);
  line(hx, hy, tx, ty, '#d8c8a0'); line(hx + 1, hy, tx + 3, ty - 2, '#a89870');           // two sticks, a loop of rope between them
  cput(tx + 1, ty + 1, '#ffffff'); cput(tx + 2, ty, '#e8f4ff');
  const cols = RING[(cyc + Math.floor(t / 30)) & 3], per = LOOP / BUB.n;
  for (let j = 0; j < BUB.count; j++) {
    const uj = 0.16 + j * 0.032, age = (u - uj) * per, life = 8.5 + hash(j, cyc, 721) * 5.5;
    if (age < 0 || age > life) continue;
    const [sx, sy] = wandTip(uj), a = age;
    const bx = x + sx + a * (4 + 3 * hash(j, cyc, 722)) + 4 * Math.sin(a * 0.9 + j), by = y + sy - 4 - a * (4.5 + 2.5 * hash(j, cyc, 723)) + 2.5 * Math.sin(a * 1.3 + j * 2);
    if (age > life - 0.25) {                                                      // pop: a little cross of spray
      const s = Math.round((age - life + 0.25) * 16) + 2;
      ctx.fillStyle = '#ffffff';
      for (const [dx, dy] of [[s, 0], [-s, 0], [0, s], [0, -s]]) ctx.fillRect(Math.round(bx) + dx, Math.round(by) + dy, 1, 1);
      continue;
    }
    const r = Math.min(4 + Math.floor(hash(j, cyc, 724) * 6), 2 + Math.floor(age * 1.6)), wob = Math.round(Math.sin(a * 3 + j));
    const spr = cols[clamp(r, 2, 9)];
    ctx.drawImage(spr, Math.round(bx) - r, Math.round(by) - r, spr.width + wob, spr.height - wob);
  }
}
// rank: one child always chases the bubbles (0), the other two thin with the crowd
const KIDS = [{ k: 24, ph: 0.1, y: 238, cx: 338, amp: 28, rank: 0 }, { k: 36, ph: 0.5, y: 241, cx: 346, amp: 22, rank: 0.7 }, { k: 24, ph: 0.72, y: 244, cx: 322, amp: 34, rank: 0.35 }];
function drawKid(K, i, t) {                            // running to and fro under the bubbles, jumping for them
  const x = K.cx + K.amp * wave(t, K.k, K.ph), left = Math.cos(TAU * (K.k * t / LOOP + K.ph)) < 0;
  const jump = Math.max(0, wave(t, K.k * 4, K.ph * 3)) > 0.82, S = KID_F[i][left ? 1 : 0];
  if (jump) drawFig(S.up, x, K.y - 3); else { const f = Math.floor(t * 9 + i) & 3; drawFig(S.w[f], x, K.y - (f & 1)); }
}

/* ----- someone who walks up to a stall, waits, and leaves with something in hand ----- */
function buildVisitor(v) {
  v.h = tall(v.y);
  v.W = [false, true].map(fl => [0, 1, 2, 3].map(f => fig(v.h, v.seed, 'walk', f, fl, v.over)));
  v.S = [false, true].map(fl => fig(v.h, v.seed, 'side', 0, fl, v.over));
}
function drawVisitor(v, t) {
  for (const a of v.times) {
    const s = t - a;
    if (s < 0 || s >= v.dur) continue;
    const walk = (v.dur - v.wait) / 2, toRight = v.x1 > v.x0, got = s >= walk + v.wait * 0.6;
    let x, fl, F;
    if (s < walk) { x = lerp(v.x0, v.x1, s / walk); fl = !toRight; F = v.W[fl ? 1 : 0][Math.floor(Math.abs(x - v.x0) / (v.h * 0.3)) & 3]; }
    else if (s < walk + v.wait) { x = v.x1; fl = !toRight; F = v.S[fl ? 1 : 0]; }
    else { x = lerp(v.x1, v.x0, (s - walk - v.wait) / walk); fl = toRight; F = v.W[fl ? 1 : 0][Math.floor(Math.abs(x - v.x1) / (v.h * 0.3)) & 3]; }
    drawFig(F, x, v.y);
    if (got) { ctx.fillStyle = v.item; ctx.fillRect(Math.round(x) + (fl ? -3 : 2), v.y - Math.round(v.h * 0.5), 2, 2); }
    return s >= walk && s < walk + v.wait;                                        // true while being served
  }
  return false;
}

/* ----- the pretzel seller: a cargo bike with a basket, a board, a lantern ----- */
const PRETZ = { x: 78, y: 237 };
const PRETZ_VISIT = { times: [100, 208], dur: 22, wait: 6, x0: -16, x1: PRETZ.x + 44, y: PRETZ.y + 2, seed: 351, item: '#c8843a' };
function circle(put, cx, cy, r, col, spoke) {
  for (let a = 0; a < 40; a++) put(cx + Math.cos(a / 40 * TAU) * r, cy + Math.sin(a / 40 * TAU) * r, col);
  if (spoke) { put(cx, cy, spoke); for (const [dx, dy] of [[1, 1], [-1, -1], [1, -1], [-1, 1], [2, 0], [-2, 0], [0, 2], [0, -2]]) if (Math.hypot(dx, dy) < r - 1) put(cx + dx, cy + dy, spoke); }
}
let PRETZ_C, PRETZ_F;
function buildPretzel() {
  const p = painter(40, 40), put = p.put;
  circle(put, 6, 34, 5, '#16161e', '#5a5a68'); circle(put, 30, 34, 5, '#16161e', '#5a5a68');
  pline(put, 6, 34, 15, 25, '#c8303a'); pline(put, 15, 25, 27, 25, '#c8303a'); pline(put, 15, 25, 19, 34, '#c8303a'); pline(put, 19, 34, 6, 34, '#8a2028');
  pline(put, 27, 25, 30, 34, '#8a2028'); pline(put, 27, 25, 28, 19, '#8a8a94'); pline(put, 25, 19, 30, 19, '#16161e');   // fork, stem, handlebar
  p.box(12, 23, 5, 2, '#2a1a12');                                                // saddle
  p.box(22, 12, 14, 8, '#a8743a'); for (let i = 22; i < 36; i++) for (let y = 12; y < 20; y++) if ((i + y) % 3 === 0) put(i, y, '#7a5226');   // the basket on the front rack
  p.box(21, 11, 16, 1, '#c8944a');
  for (const [bx, by] of [[23, 8], [27, 7], [31, 8], [25, 9], [29, 9], [33, 9]]) stamp(p, ['.b.', 'bBb', 'b.b'], { b: '#8a4a1e', B: '#d89040' }, bx, by);   // pretzels
  pline(put, 8, 30, 8, 2, '#3a3a48'); p.box(0, 0, 29, 9, '#f4e8c8'); for (let i = 0; i < 29; i++) { put(i, 0, '#8a4a1e'); put(i, 8, '#8a4a1e'); }   // the board
  const c = p.done();
  text(p.x, 'BREZEL', 3, 2, '#7a3a16');
  PRETZ_C = c;
  const over = { coat: NIGHT('#e8e0d0'), hat: 3, hatCol: '#e8e0d0', scarf: '#2a5ab8', bag: 0, long: true };
  PRETZ_F = [fig(tall(PRETZ.y), 350, 'side', 0, false, over), fig(tall(PRETZ.y), 350, 'sideUp', 0, false, over)];
  buildVisitor(PRETZ_VISIT);
}
function drawPretzel(t, alone) {                      // alone: the bike stands there lit, nobody selling or buying
  const { x, y } = PRETZ;
  ctx.drawImage(PRETZ_C, x - 6, y - 39);
  cput(x + 23, y - 22, '#ffe8a0'); halo(HL.lantern, x + 23, y - 22, 0.85 + 0.15 * wave(t, 420, 0.1));       // the lantern on the handlebar
  if (alone) return;
  const serving = drawVisitor(PRETZ_VISIT, t), hold = serving || frac(t / 6) < 0.45;
  drawFig(PRETZ_F[hold ? 1 : 0], x + 34, y + 1);
  if (hold) stamp({ put: cput }, ['.b.', 'bBb', 'b.b'], { b: '#8a4a1e', B: '#d89040' }, x + 38, y - 23 - (step(t, 480) & 1));   // "fresh pretzels!"
}

/* ----- the hot-drink cart: striped awning, an urn steaming on the counter, a string of bulbs ----- */
const CART = { x: 366, y: 235, w: 34, h: 36 };
const CART_VISIT = { times: [12, 72, 132, 196], dur: 20, wait: 6, x0: W + 16, x1: CART.x + CART.w + 7, y: CART.y + 1, seed: 361, item: '#f4f0e8' };
let CART_BACK, CART_FRONT, CART_F;
function buildCart() {
  const b = painter(CART.w, CART.h), f = painter(CART.w, CART.h);
  for (let y = 0; y < 8; y++) for (let i = 0; i < CART.w; i++) {                   // awning, scalloped
    if (y === 7 && (i % 6 === 0 || i % 6 === 5)) continue;
    b.put(i, y, y === 0 ? '#f8f0dc' : (Math.floor(i / 4) & 1) ? (y > 5 ? '#1e6a6a' : '#2a8a8a') : (y > 5 ? '#c8c0ac' : '#f0e8d4'));
  }
  for (let y = 8; y < CART.h - 4; y++) { b.put(2, y, '#6a5a48'); b.put(CART.w - 3, y, '#3a3028'); }
  b.box(4, 9, CART.w - 8, 9, '#2a1c1a');                                          // the back board with a shelf of cups
  for (let i = 6; i < CART.w - 6; i += 3) { b.put(i, 13, '#f4f0e8'); b.put(i, 14, '#d8d0c0'); }
  for (let i = 4; i < CART.w - 4; i++) b.put(i, 15, '#6a4a2e');
  for (let i = 1; i < CART.w - 1; i++) { f.put(i, 20, '#e8c890'); f.put(i, 21, '#a8804a'); }   // counter
  for (let y = 22; y < CART.h - 3; y++) for (let i = 2; i < CART.w - 2; i++) f.put(i, y, y === 22 ? '#3a1410' : (i % 5 === 1) ? '#6a1e1c' : y < 27 ? '#9a2e28' : '#8a2824');
  circle(f.put, 7, CART.h - 4, 3, '#16161e', '#5a5a68'); circle(f.put, CART.w - 8, CART.h - 4, 3, '#16161e', '#5a5a68');
  for (let y = 11; y < 20; y++) for (let i = 6; i < 12; i++) f.put(i, y, y === 11 ? '#e8ecf4' : i === 6 ? '#c8ccd8' : i === 11 ? '#5a5e6e' : '#8a8ea0');   // the urn
  f.put(8, 10, '#3a3a48'); f.put(9, 10, '#3a3a48'); f.put(12, 17, '#c8a040'); f.put(13, 17, '#c8a040'); f.put(13, 18, '#8a6a20');
  for (const i of [17, 20, 23]) { f.put(i, 18, '#f4f0e8'); f.put(i, 19, '#d8d0c0'); f.put(i + 1, 18, '#f4f0e8'); f.put(i + 1, 19, '#c8c0b0'); }   // cups
  CART_BACK = b.done(); const fc = f.done();
  text(f.x, 'KAKAO', 8, 24, '#f8e8c0');
  CART_FRONT = fc;
  CART_F = [fig(tall(CART.y) - 1, 360, 'side', 0, false, { coat: NIGHT('#2a6e4e'), hat: 1, hatCol: '#c8303a', bag: 0, long: false, scarf: '#e8e4dc' }),
    fig(tall(CART.y) - 1, 360, 'sideUp', 0, false, { coat: NIGHT('#2a6e4e'), hat: 1, hatCol: '#c8303a', bag: 0, long: false, scarf: '#e8e4dc' })];
  buildVisitor(CART_VISIT);
}
function drawCart(t, alone) {                         // alone: the cart stays lit and the urn steams, nobody behind or at the counter
  const { x, y } = CART, top = y - CART.h + 1;
  halo(HL.cart, x + 17, top + 14, 0.9 + 0.06 * wave(t, 500, 0.3));
  ctx.drawImage(CART_BACK, x, top);
  if (!alone) {
    const serving = drawVisitor(CART_VISIT, t);
    drawFig(CART_F[serving || frac(t / 7.5 + 0.2) < 0.2 ? 1 : 0], x + 21, y - 2);  // the vendor behind the counter
  }
  ctx.drawImage(CART_FRONT, x, top);
  const s = step(t + 0.07, 480);
  for (let k = 0; k < 6; k++) cput(x + 4 + k * 5, top + 8 + (k & 1), hash(k, s, 731) < 0.12 ? '#8a6a30' : k % 3 === 1 ? '#ff8a5a' : '#ffe8a0');   // bulbs
  puffs(STEAM, 9, t, x + 9, top + 8, 20, 7, 3, 0.55);
}

/* ----- the pedicab: two passes; the bike tour: one; the marching brass band: the loop's street parade ----- */
const PEDI = [{ a: 6, b: 34, dir: 1 }, { a: 124, b: 152, dir: -1 }];
const PEDI_Y = 249;
const TOUR = { a: 150, b: 184, y: 241, n: 6, gap: 27, v: 21 };
let PEDI_SPR, BIKE_SPR, BAND_F;
function buildWheels() {
  PEDI_SPR = [false, true].map(fl => [0, 1].map(f => {                            // a white-and-lime shell on three wheels
    const p = painter(40, 26), X = i => fl ? 39 - i : i, put = (i, y, col) => p.put(X(i), y, col);
    for (let y = 2; y < 18; y++) for (let i = 1; i < 24; i++) {
      const d = Math.hypot((i - 12) / 12, (y - 10) / 8.5);
      if (d > 1) continue;
      const win = i > 6 && i < 21 && y > 5 && y < 12;
      put(i, y, win ? '#1a2238' : d > 0.86 ? '#7ab000' : y < 6 ? '#f4f8ec' : y > 13 ? '#a8f000' : '#e4ecd8');
    }
    for (const hx of [10, 16]) { put(hx, 8, '#e0b090'); put(hx + 1, 8, '#e0b090'); put(hx, 7, hx === 10 ? '#c8303a' : '#3a2a1a'); put(hx + 1, 7, hx === 10 ? '#c8303a' : '#3a2a1a'); for (let i = -1; i <= 2; i++) for (let y = 9; y < 12; y++) put(hx + i, y, hx === 10 ? '#3a4a8a' : '#8a5a3a'); }
    circle(put, 9, 21, 4, '#16161e', f ? '#8a8a98' : '#4a4a58'); circle(put, 33, 21, 4, '#16161e', f ? '#4a4a58' : '#8a8a98');
    pline(put, 22, 16, 33, 21, '#3a3a48'); pline(put, 28, 12, 33, 21, '#3a3a48'); pline(put, 33, 21, 34, 10, '#5a5a68'); pline(put, 32, 10, 36, 10, '#16161e');
    for (let y = 4; y < 13; y++) for (let i = 27; i < 31; i++) put(i + (y < 8 ? 1 : 0), y, y < 6 ? '#f4f0e8' : y < 7 ? '#e0b090' : '#e86a2a');   // the driver: helmet, face, jacket
    pline(put, 29, 13, f ? 31 : 28, 17, '#1a1a26'); pline(put, f ? 31 : 28, 17, f ? 30 : 29, 21, '#1a1a26');   // pedalling
    pline(put, 31, 8, 34, 10, '#b84a1a'); put(37, 12, '#fff8d0'); put(0, 14, '#ff3020');
    return p.done();
  }));
  const JACKETS = ['#a8f000', '#c84a3a', '#3a5a9a', '#e0a838', '#8a8a94', '#b86a8a'].map(NIGHT);
  BIKE_SPR = JACKETS.map((jc, k) => [0, 1].map(f => {
    const p = painter(22, 24), put = p.put;
    circle(put, 5, 18, 4, '#16161e', f ? '#8a8a98' : '#4a4a58'); circle(put, 16, 18, 4, '#16161e', f ? '#4a4a58' : '#8a8a98');
    pline(put, 5, 18, 9, 12, '#9a9aa8'); pline(put, 9, 12, 15, 12, '#9a9aa8'); pline(put, 9, 12, 11, 18, '#9a9aa8'); pline(put, 15, 12, 16, 18, '#6a6a78'); pline(put, 15, 12, 15, 9, '#6a6a78');
    put(14, 9, '#16161e'); put(16, 9, '#16161e'); p.box(7, 11, 3, 1, '#2a1a12');
    for (let y = 4; y < 11; y++) for (let i = 8; i < 12; i++) put(i + Math.round((10 - y) * 0.45), y, jc);              // leaning torso
    pline(put, 12, 6, 15, 9, darker(jc)); p.box(12, 1, 3, 3, '#e0b090'); p.box(12, 0, 3, 1, k ? '#e8e4dc' : '#ffe040'); put(11, 1, k ? '#e8e4dc' : '#ffe040');
    pline(put, 9, 11, f ? 12 : 9, 15, '#1a1a26'); pline(put, f ? 12 : 9, 15, f ? 11 : 10, 19, '#1a1a26');
    put(18, 11, '#fff8d0'); if (k === TOUR.n - 1) put(1, 13, '#ff3020');
    if (!k) { pline(put, 7, 11, 5, 0, '#8a8a94'); p.box(0, 0, 5, 3, '#ff8a3a'); }                                       // the guide's pennant
    return p.done();
  }));
  const uni = { coat: NIGHT('#1e2e6a'), legs: '#101018', hat: 3, hatCol: '#c8303a', scarf: '#e8c040', bag: 0, long: false };
  BAND_F = [0, 1, 2, 3, 4, 5, 6, 7].map(k => [0, 1, 2, 3].map(f => fig(tall(BAND_Y) - (k % 3 === 1 ? 1 : 0), 370 + k, 'walk', f, false, uni)));
}
function drawPedicab(t) {
  for (const P of PEDI) {
    if (t < P.a || t >= P.b) continue;
    const u = (t - P.a) / (P.b - P.a), x = Math.round(P.dir > 0 ? -44 + u * (W + 88) : W + 44 - u * (W + 88)), f = Math.floor(t * 5) & 1;
    ctx.drawImage(PEDI_SPR[P.dir > 0 ? 0 : 1][f], x - 20, PEDI_Y - 25);
    halo(HL.head, x + P.dir * 18, PEDI_Y - 13, 0.9);
  }
}
function drawTour(t) {
  if (t < TOUR.a || t >= TOUR.b) return;
  for (let k = TOUR.n - 1; k >= 0; k--) {
    const x = Math.round(-24 + (t - TOUR.a) * TOUR.v - k * TOUR.gap + 3 * Math.sin(t * 0.9 + k * 2));
    if (x < -24 || x > W + 24) continue;
    ctx.drawImage(BIKE_SPR[k][Math.floor(t * 6 + k) & 1], x - 11, TOUR.y - 23 + (k & 1));
    halo(HL.headS, x + 8, TOUR.y - 12, 0.8);
  }
}

const BAND_Y = 242;                                   // the band marches across the front of the square (heads above row 216)
const GOLD = '#f0c040', GOLD_D = '#a87a1c', GLINT = '#fff8c8', UNI = '#1c2a5c';
let BASS_C, BELL_C;
function buildBand() {
  const disc = (r, fn) => { const p = painter(r * 2 + 1, r * 2 + 1); for (let y = 0; y <= r * 2; y++) for (let i = 0; i <= r * 2; i++) { const d = Math.hypot(i - r, y - r); if (d <= r + 0.3) p.put(i, y, fn(d, i, y)); } return p.done(); };
  BASS_C = disc(7, (d, i, y) => d > 6 ? '#c8303a' : d > 5.2 ? GOLD : (i + y < 12 ? '#f8f4e8' : '#d8d0c0'));
  BELL_C = disc(5, (d, i, y) => d > 4.2 ? (i + y < 9 ? GLINT : GOLD) : d > 2.5 ? GOLD_D : '#5a3a0c');
}
function drawBand(t) {
  if (t < BAND.pre || t >= BAND.b) return;
  // the drum major steps in at the left edge and marks time until the band strikes up, then they march across
  const marking = t < BAND.a, d = marking ? 70 * smooth(BAND.pre, BAND.pre + 4, t) : 70 + (t - BAND.a) * 14.5;
  const phase = marking ? (t - BAND.pre) * 1.6 : d / 9, idx = Math.floor(phase), f = idx & 3, beat = (idx >> 1) & 1, s8 = Math.floor(t * 8);
  for (let k = 7; k >= 0; k--) {
    const x = Math.round(-30 + d - k * 25);
    if (x < -30 || x > W + 30) continue;
    const F = BAND_F[k][f], y = BAND_Y - (f & 1), top = y - F.h + 1, o = n => top + Math.round(n * F.h / 30);   // rows, for a 30 px bandsman
    if (k === 4) ctx.drawImage(BELL_C, x - 8, top - 9);                            // the sousaphone's bell rides behind his head
    drawFig(F, x, y);
    ctx.fillStyle = '#f4f0e8'; ctx.fillRect(x + 1, top - 2, 1, 2);                 // plumes
    ctx.fillStyle = GOLD; ctx.fillRect(x - 1, o(9), 1, 1); ctx.fillRect(x - 1, o(12), 1, 1);   // buttons
    if (k === 0) {                                                                 // the drum major pumps his mace
      const up = beat ? 0 : 4;
      line(x + 4, o(14), x + 7, top - 6 + up, GOLD); ctx.fillStyle = GLINT; ctx.fillRect(x + 6, top - 8 + up, 3, 3);
    } else if (k < 3) {                                                            // trumpets
      line(x + 1, o(7), x + 6, o(4), UNI);
      ctx.fillStyle = GOLD; ctx.fillRect(x + 4, o(3), 8, 1); ctx.fillRect(x + 11, o(3) - 1, 2, 3); ctx.fillStyle = GOLD_D; ctx.fillRect(x + 7, o(3) + 1, 2, 1);
      if (hash(k, s8, 741) < 0.2) cput(x + 12, o(3) - 1, '#ffffff');
    } else if (k === 3) {                                                          // trombone: the slide goes out and in
      const L = beat ? 14 : 9;
      line(x + 1, o(7), x + 6, o(4), UNI);
      ctx.fillStyle = GOLD; ctx.fillRect(x + 4, o(3), L, 1); ctx.fillRect(x + 4, o(3) + 2, L - 3, 1); ctx.fillRect(x - 5, o(3) - 1, 2, 3); ctx.fillRect(x - 4, o(3), 8, 1);
    } else if (k === 4) {                                                          // sousaphone: the tube wraps round him
      for (let a = 0; a < 26; a++) { const an = a / 26 * TAU; if (Math.sin(an) < -0.75) continue; cput(Math.round(x + Math.cos(an) * 6), Math.round(o(13) + Math.sin(an) * 7), a % 5 ? GOLD : GLINT); }
    } else if (k === 5) {                                                          // snare
      const dy = o(15);
      ctx.fillStyle = '#f4f0e8'; ctx.fillRect(x + 3, dy, 8, 1); ctx.fillStyle = '#c8303a'; ctx.fillRect(x + 3, dy + 1, 8, 4);
      ctx.fillStyle = GOLD; for (let i = 0; i < 8; i += 2) ctx.fillRect(x + 3 + i, dy + 2 + (i & 2 ? 1 : 0), 1, 1);
      const sA = s8 & 1; line(x + 2, o(10), x + 5, dy - (sA ? 1 : 4), '#d8c8a0'); line(x + 4, o(10), x + 8, dy - (sA ? 4 : 1), '#d8c8a0');
    } else if (k === 6) {                                                          // bass drum
      ctx.drawImage(BASS_C, x - 1, o(6));
      const hit = beat && frac(phase / 2) < 0.25; line(x + 2, o(8), x + (hit ? 6 : 10), o(hit ? 13 : 5), '#d8c8a0'); cput(x + (hit ? 6 : 10), o(hit ? 13 : 5), '#f4f0e8');
    } else {                                                                       // cymbals
      const gap = beat ? 1 : 5;
      ctx.fillStyle = GOLD; ctx.fillRect(x + 5, o(5), 1, 8); ctx.fillRect(x + 5 + gap, o(5), 1, 8);
      line(x + 1, o(7), x + 5, o(9), UNI);
      if (beat && frac(phase / 2) < 0.3) { ctx.fillStyle = '#ffffff'; ctx.fillRect(x + 5, o(5) - 1, 2, 1); halo(HL.flashS, x + 6, o(8), 0.9); }
    }
  }
}

/* ================= Lindens from Unter den Linden: boughs over the near corners, yellow in October ================= */
const LIND = ['#1c1a0c', '#3a3210', '#5e5216', '#8a781c', '#b89e28', '#e0c43a', '#f8e468'];
const CROWNS = [   // [cx, cy, rx, ry]
  [10, 8, 34, 20], [48, 4, 30, 16], [84, 10, 26, 14], [22, 34, 30, 18], [62, 28, 24, 14], [6, 58, 22, 16], [40, 52, 20, 11], [100, 26, 14, 9],
  [470, 6, 30, 17], [434, 2, 26, 12], [462, 30, 24, 15], [404, 8, 14, 8], [478, 52, 14, 12],
];
const LEAFPX = [];                                    // every foliage pixel, for the rustle
function renderLindens() {
  const p = painter(W, H), put = p.put;
  // the left tree's trunk and boughs, the right tree's boughs coming in from beyond the frame
  for (let y = 40; y < H; y++) { const cx = 7 + (H - y) * 0.03, hw = 5 + (y - 40) * 0.012; for (let i = Math.floor(cx - hw); i <= cx + hw; i++) { const u = (i - cx + hw) / (2 * hw); put(i, y, u > 0.72 ? '#4a3a2c' : hash(i, y >> 2, 801) < 0.18 ? '#0c0a0e' : u < 0.3 ? '#16121a' : '#241c20'); } }
  for (const [x0, y0, x1, y1] of [[10, 70, 52, 30], [12, 60, 86, 14], [9, 80, 30, 46], [30, 46, 98, 26], [W, 30, 440, 12], [W, 44, 456, 34], [470, 20, 408, 9]]) {
    pline(put, x0, y0, x1, y1, '#1e181c'); pline(put, x0, y0 + 1, x1, y1 + 1, '#120e12');
  }
  // foliage in soft clumps: dark olive on top, gold underneath where the lamps and the show light it
  const vn = (x, y, s, seed) => {
    const gx = Math.floor(x / s), gy = Math.floor(y / s), fx = x / s - gx, fy = y / s - gy, sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
    return lerp(lerp(hash(gx, gy, seed), hash(gx + 1, gy, seed), sx), lerp(hash(gx, gy + 1, seed), hash(gx + 1, gy + 1, seed), sx), sy);
  };
  const seen = new Set();
  for (const [cx, cy, rx, ry] of CROWNS) for (let y = Math.floor(cy - ry); y <= cy + ry; y++) for (let x = Math.floor(cx - rx); x <= cx + rx; x++) {
    if (x < 0 || x >= W || y < 0) continue;
    const d = Math.hypot((x - cx) / rx, (y - cy) / ry) + (vn(x, y, 6, 802) - 0.5) * 0.7;
    if (d > 1 || (d > 0.5 && vn(x, y, 3, 803) < 0.28)) continue;                    // ragged edges, gaps of sky near them
    let v = 0.12 + 0.62 * (y - cy + ry) / (2 * ry) + (vn(x, y, 5, 804) - 0.5) * 0.6 + (vn(x, y, 2, 806) - 0.5) * 0.3;
    if (hash(x, y, 807) < 0.05) v += 0.3;
    put(x, y, LIND[clamp(Math.floor(v * 6 + (bayer(x, y) - 0.5) * 0.6), 0, 6)]);
    const key = y * W + x;
    if (!seen.has(key)) { seen.add(key); LEAFPX.push(x, y); }
  }
  return p.done();
}
function drawLindens(t) {
  ctx.drawImage(LIND_C, 0, 0);
  const s = step(t + 0.05, 720), n = LEAFPX.length >> 1, gust = 0.6 + 0.4 * wave(t, 9, 0.1);
  for (let i = 0; i < 300 * gust; i++) {                                           // leaves turning in the breeze
    const k = Math.floor(hash(i, s, 805) * n) * 2;
    ctx.fillStyle = (i & 3) === 0 ? '#2a260e' : (i & 3) === 1 ? '#f8e468' : '#d0b030';
    ctx.fillRect(LEAFPX[k], LEAFPX[k + 1], 1 + (i & 1), 1);
  }
}

/* ================= Falling linden leaves (3 depths): the scene's own 'leaves' ================= */
const LF_SHAPES = [[['##']], [['.##', '#v#', '##.', 'v..'], ['##.', '.vv'], ['.#.', '#v.', '.##', '..v']],
  [['..###.', '.##v##', '##v###', '#v###.', 'v###..', 'v.....'], ['.####.', 'vvvvvv', '.####.'], ['..##', '.#v#', '##v#', '#v#.', 'v#..', 'v...']]];
const LF_COLS = [['#e8c83a', '#a8842a'], ['#f4e070', '#b89a30'], ['#d8a030', '#8a5a1e']];
const LF_DEF = [{ count: 46, nMin: 7, nMax: 10, amp: 4, drift: -26, y1: [150, 186] }, { count: 28, nMin: 9, nMax: 12, amp: 8, drift: -44, y1: [200, 254] },
  { count: 11, nMin: 12, nMax: 15, amp: 13, drift: -64, y1: [H + 8, H + 8] }];
let LF_SPR;
const LEAVES = LF_DEF.map((L, li) => { const r = rng(820 + li), out = []; for (let i = 0; i < L.count; i++) out.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r(), ks: 30 + Math.floor(r() * 30), p2: r(), kf: 40 + Math.floor(r() * 50), col: Math.floor(r() * 3) }); return out; });
function buildLeaves() {
  LF_SPR = LF_SHAPES.map((shapes, d) => LF_COLS.map(([a, b]) => shapes.map((rows, k) => sprite(rows, { '#': d === 0 ? mixHex(a, '#1a1c3a', 0.35) : k === 1 ? b : a, v: k === 1 ? darker(b, 0.3) : b }))));
}
function drawLeaves(depth, t) {
  const L = LF_DEF[depth];
  for (const l of LEAVES[depth]) {
    const { cyc, u } = cycle(t, l.n, l.ph), sw = wave(t, l.ks, l.p2);
    const yEnd = lerp(L.y1[0], L.y1[1], hash(l.id, cyc, 6)), y = -8 + u * (yEnd + 8) - Math.abs(sw) * (depth + 1);
    const x = 20 + hash(l.id, cyc, 5) * (W + 40) + u * L.drift + sw * L.amp;
    const S = LF_SPR[depth][(l.col + cyc) % 3], spr = S[depth ? Math.floor(frac(l.kf * t / LOOP + l.ph) * 4) % S.length : 0];
    ctx.globalAlpha = L.y1[0] < H && u > 0.9 ? (1 - u) * 10 : 1;
    ctx.drawImage(spr, Math.round(x) - (spr.width >> 1), Math.round(y));
  }
  ctx.globalAlpha = 1;
}

/* ================= Confetti cannons either side of the gate, fired on the finale's four big beats ================= */
const SHOTS = [0, 4, 8, 12].map(k => FINALE.a + k * CUT);
const CONF_COLS = ['#ff5aa8', '#ffe03a', '#5cf0ff', '#ffffff', '#a8f000', '#ff8a3a'];
const CONF_N = 70;
function drawConfetti(t) {
  for (let s = 0; s < SHOTS.length; s++) {
    const tau = t - SHOTS[s];
    if (tau < 0 || tau >= 8.5) continue;
    for (let side = 0; side < 2; side++) {
      const x0 = side ? 336 : 144, dir = side ? -1 : 1;
      if (tau < 0.35) halo(HL.flash, x0, G - 4, 1 - tau / 0.35);
      for (let i = 0; i < CONF_N; i++) {
        const id = s * 1000 + side * 500 + i, h1 = hash(id, 1, 751), h2 = hash(id, 2, 751), h3 = hash(id, 3, 751);
        if (((Math.floor(tau * 9) + i) & 3) === 0) continue;                       // the paper turns edge-on
        const rise = (60 + 80 * h1) * (1 - Math.exp(-tau * 1.7)), fall = Math.max(0, tau - 0.6) * (9 + 10 * h2);
        const y = G - 4 - rise + fall;
        if (y > G + 8 + 46 * h3) continue;                                         // it has landed on the square
        const x = x0 + dir * (-18 + 110 * h2) * (1 - Math.exp(-tau * 1.1)) + Math.sin(tau * 3.1 + h3 * 6.3) * 2.5 * Math.min(1, tau);
        ctx.globalAlpha = tau > 7 ? (8.5 - tau) / 1.5 : 1;
        ctx.fillStyle = CONF_COLS[i % 6];
        ctx.fillRect(Math.round(x), Math.round(y), 1 + (i & 1), 1 + (i % 3 === 0 ? 1 : 0));
      }
    }
  }
  ctx.globalAlpha = 1;
}

/* ================= Hooded crows on the left gatehouse's roof; an advertising column by the house ================= */
// They hop and peck, and go up in a flutter when the band strikes up and on the finale's first flash.
const ROOF_CROWS = [{ x: 95, y: 140 }, { x: 103, y: 138 }, { x: 112, y: 139 }, { x: 121, y: 143 }, { x: 137, y: 146 }, { x: 149, y: 146 }];
const SCARES = [[BAND.a + 5, BAND.a + 12, 1], [FINALE.a, FINALE.a + 7], [121, 126]];   // [from, to, the band's doing]
function drawRoofCrows(t, nobody) {                    // nobody: no band strikes up, so that scare never comes
  ROOF_CROWS.forEach((c, i) => {
    let x = c.x, y = c.y, fly = 0;
    for (const [a, b, band] of SCARES) {
      if (band && nobody) continue;
      const u = (t - a - i * 0.25) / (b - a);
      if (u > 0 && u < 1) { fly = 1; x += Math.round(Math.sin(u * TAU) * (8 + i * 2) * (i & 1 ? 1 : -1)); y -= Math.round(Math.sin(u * Math.PI) * (14 + i * 3)); }
    }
    const s = step(t + i * 0.37, 240), hop = !fly && hash(i, s, 761) < 0.22 ? 1 : 0, peck = !fly && hash(i, s, 762) < 0.2, left = hash(i, s >> 2, 763) < 0.5;
    y -= hop;
    ctx.fillStyle = '#8a8a98'; ctx.fillRect(x, y - 1, 2, 2);                        // grey body
    ctx.fillStyle = '#0a0a12';
    ctx.fillRect(left ? x - 1 : x + 2, y - (peck ? 0 : 2), 1, 1 + (peck ? 0 : 1));   // black head
    ctx.fillRect(left ? x + 2 : x - 1, y, 1, 1);                                   // tail
    if (fly) { const up = frac(t * 5 + i * 0.3) < 0.5; ctx.fillRect(x - 2, y - (up ? 3 : 0), 2, 1); ctx.fillRect(x + 2, y - (up ? 3 : 0), 2, 1); }
  });
}
let PILLAR_C;
const PILLAR = { x: 71, y: 199 };
function buildPillar() {                               // a Litfass column: a green cap, posters pasted round the drum
  const p = painter(11, 29), POST = ['#e8dcc0', '#c83a3a', '#e8c040', '#3a6ab8', '#e8dcc0', '#2a8a6a', '#d86a9a'];
  [[5, 5], [3, 7], [1, 9], [0, 10], [1, 9]].forEach(([a, b], k) => { for (let i = a; i <= b; i++) p.put(i, k, i < 4 ? '#3a6a52' : i < 7 ? '#2a4a3a' : '#1a2e26'); });
  for (let y = 5; y < 27; y++) for (let i = 1; i <= 9; i++) {
    const band = Math.floor((y - 5) / 7), sheet = i < 4 ? 0 : i < 7 ? 1 : 2, col = POST[(band * 3 + sheet) % POST.length];
    const edge = (y - 5) % 7 === 6 || i === 4 || i === 7, mark = hash(i, y, 771) < 0.28 && (y - 5) % 7 > 1 && (y - 5) % 7 < 5;
    p.put(i, y, edge ? '#2a2630' : mark ? '#1e1a26' : i > 7 ? darker(col, 0.45) : i > 5 ? darker(col, 0.2) : col);
  }
  for (let i = 0; i <= 10; i++) { p.put(i, 27, '#3a3a48'); p.put(i, 28, '#1e1e2a'); }
  PILLAR_C = p.done();
}

/* ================= The sausage seller: a grill worn on a harness under a red-and-white umbrella, a Berlin street sight ================= */
const WURST = { x: 451, y: 227 };
const WURST_VISIT = { times: [46, 148, 220], dur: 18, wait: 6, x0: W + 16, x1: WURST.x + 15, y: WURST.y + 1, seed: 381, item: '#e8d8b0' };
let WURST_C, WURST_F;
function buildWurst() {
  const p = painter(27, 24);
  [2, 5, 8, 10, 12, 13].forEach((hw, y) => { for (let i = -hw; i <= hw; i++) p.put(13 + i, y, (Math.floor((i + 14) / 4.5) & 1) ? (i > 4 ? '#8a2028' : '#c8303a') : (i > 4 ? '#b8b4a8' : '#f0ece0')); });
  for (let y = 6; y < 13; y++) for (let i = 0; i < 27; i++) if (y < 12 || (i % 4) !== 3) p.put(i, y, y === 6 ? '#c8303a' : '#f0ece0');   // the valance
  for (let y = 13; y < 24; y++) p.put(8, y, '#8a8a94');                              // the pole on his back
  const c = p.done();
  text(p.x, 'WURST', 4, 7, '#b0222c');
  WURST_C = c;
  const over = { coat: NIGHT('#e8e0d0'), legs: '#1a1a26', hat: 3, hatCol: '#c8303a', scarf: null, bag: 0, long: false };
  WURST_F = [fig(tall(WURST.y), 380, 'side', 0, false, over), fig(tall(WURST.y), 380, 'sideUp', 0, false, over)];
  buildVisitor(WURST_VISIT);
}
function drawWurst(t) {
  const { x, y } = WURST, h = tall(y);
  halo(HL.lantern, x + 7, y - 12, 0.7 + 0.2 * wave(t, 380, 0.4));
  ctx.drawImage(WURST_C, x - 12, y - h - 13);
  const serving = drawVisitor(WURST_VISIT, t);
  drawFig(WURST_F[serving ? 1 : 0], x, y);
  line(x, y - h + 6, x + 4, y - 12, '#3a3a48');                                     // the harness
  ctx.fillStyle = '#2a2a34'; ctx.fillRect(x + 3, y - 12, 9, 3); ctx.fillStyle = '#5a5a68'; ctx.fillRect(x + 3, y - 12, 9, 1);
  const s = step(t + 0.03, 960);
  for (let k = 0; k < 4; k++) cput(x + 4 + k * 2, y - 11, hash(k, s, 781) < 0.5 ? '#ff8a2a' : '#c8401a');   // embers
  ctx.fillStyle = '#9a4a22'; ctx.fillRect(x + 4, y - 13, 3, 1); ctx.fillRect(x + 8, y - 13, 3, 1);          // sausages
  puffs(STEAM, 7, t + 3, x + 8, y - 14, 20, 9, 3, 0.4);
}

/* ================= The picked weather (site/js/engine/weather.js) ================= */
// Berlin draws its own leaves; rain, snow and the rest fall behind the gate, onto the square and past the bottom of
// the frame, and catch the lamps, the cart and the gate's glow
const WEATHER = weatherPlans(['leaves']);
const WX_LAND = [[150, 186], [200, 254], [H + 4, H + 4]];
const WX_LIGHTS = [
  { x: 50, y: 184, rx: 30, ry: 26 }, { x: 434, y: 184, rx: 30, ry: 26 }, { x: 116, y: 172, rx: 14, ry: 14 }, { x: 363, y: 172, rx: 14, ry: 14 },
  { x: CART.x + 17, y: CART.y - 22, rx: 20, ry: 14 }, { x: CX, y: 150, rx: 84, ry: 44, col: '#f0e0ff' },
];
const WX = { land: WX_LAND, lights: WX_LIGHTS };
const WX_BB = { land: WX_LAND, lights: [...WX_LIGHTS, { x: BB.x + (BB.w >> 1), y: BB.y + 6, rx: 30, ry: 13, col: '#fff0d0' }] };

function renderVignette() {
  const [c, x] = makeCanvas(W, H), img = x.createImageData(W, H), d = img.data;
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.78, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H / 2) / (H / 2) * 0.9));
    const q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q <= 0) continue;
    const o = (y * W + i) * 4; d[o] = 4; d[o + 1] = 4; d[o + 2] = 18; d[o + 3] = Math.round(q * 0.42 * 255);
  }
  x.putImageData(img, 0, 0);
  return c;
}

/* ================= Pre-render everything static ================= */
let HL, SKY_C, MOON_C, CLOUDS, FAR_C, BUS_C, VAN_C, HOUSE_C, BB_C, AD_C, PITCH, GROUND_C, VEIL_C, POOL, PUFF, LIND_C, VIG_C;
const SQUARE = [];                                      // everything standing or moving on the square, sorted far to near: { y, rank, draw }
function prerender() {
  HWIN.length = 0; LEAFPX.length = 0; SQUARE.length = 0;
  HL = {
    moon: makeHalo(20, '#e8e4d0', 0.14, 1.4), farWarm: makeHalo(8, '#ffd070', 0.3), blue: makeHalo(9, '#5a80ff', 0.6, 1.2),
    column: makeHalo(7, '#ffd870', 0.4, 1.4), passage: makeHalo(20, '#ffb060', 0.2, 1.5), wing: makeHalo(22, '#ffb868', 0.16, 1.5),
    quad: makeHalo(26, '#d8ffe8', 0.2, 1.5), shop: makeHalo(30, '#ffc070', 0.14, 1.5), amber: makeHalo(5, '#ffb020', 0.5, 1.2),
    bbWash: makeHalo(30, '#fff0d0', 0.1, 1.5), bbLamp: makeHalo(6, '#fff0c8', 0.5, 1.5),
    lamp: makeHalo(22, '#ffc878', 0.22, 1.6), lampBig: makeHalo(46, '#ffc878', 0.2, 1.7), lampCore: makeHalo(8, '#fff0c8', 0.5, 1.3),
    flash: makeHalo(11, '#ffffff', 0.75, 1.2), flashS: makeHalo(6, '#ffffff', 0.7, 1.1), lantern: makeHalo(9, '#ffc060', 0.4, 1.3),
    cart: makeHalo(26, '#ffb860', 0.22, 1.5), head: makeHalo(12, '#fff4d0', 0.3, 1.3), headS: makeHalo(6, '#fff4d0', 0.3, 1.2), wash: {},
  };
  for (const [id, col] of Object.entries(ACT_COL)) HL.wash[id] = makeHalo(110, col, id === 'count' ? 0.1 : 0.13, 1.5, 5);
  SKY_C = renderSky(); MOON_C = renderMoon(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer);
  FAR_C = renderFar(); BUS_C = sprite(BUS_ROWS, BUS_KEY); VAN_C = sprite(VAN_ROWS, VAN_KEY, true);
  GATE_LUM = renderFront(LUM); GATE_SUN = renderFront(SAND, true); GATE_BASE = renderGateBase();
  QUAD_C = renderQuadriga(QC); QUAD_W = renderQuadriga(QC.map(() => '#ffffff'));
  [PJ_C, PJ_X] = makeCanvas(PJ.w, PJ.h); PAT = renderPatterns();
  HOUSE_C = renderHouses(); BB_C = renderBanner(); AD_C = renderAd(); PITCH = makePitch(BB.w, BB.h, { accent: '#a8f000' });
  GROUND_C = renderGround(); VEIL_C = renderVeil();
  POOL = { big: makePool(44, 9, '#ffc070', 0.34), small: makePool(24, 5, '#ffc070', 0.3), pass: makePool(12, 4, '#ffb060', 0.4) };
  for (const L of LAMPS) L.c = renderLamp(L);
  PUFF = [null]; for (let r = 1; r <= 5; r++) PUFF.push(makePuff(r, '#f4eee6', '#c4bcc4'));
  buildWatchers(); buildWalkers(); buildBusker(); buildDance(); buildPhotogs(); buildBubbles(); buildPretzel(); buildCart(); buildWheels(); buildBand(); buildLeaves(); buildPillar(); buildWurst();
  LIND_C = renderLindens(); VIG_C = renderVignette();
  // rank 0 stays at every share above 0: the acts, the stalls with their sellers and customers, the vehicles, the
  // band, the furniture. `bare` is what an entry leaves on the square at crowd 0, when nobody at all is out: the lamps,
  // the advertising column, the pretzel bike and the cocoa cart (lit, unattended). An entry without it goes with its
  // people: the busker with his guitar, case and notes, the dancers, the bubble artist with her bucket and bubbles,
  // the sausage seller with the grill and umbrella he wears, the pedicab, the bike tour, the band.
  for (const w of WATCHERS) SQUARE.push({ y: w.y, rank: w.rank, draw: t => drawWatcher(w, t) });
  for (const w of WALKERS) SQUARE.push({ y: w.y, rank: w.rank, draw: t => drawWalker(w, t) });
  for (const L of LISTENERS) SQUARE.push({ y: L.y, rank: L.rank, draw: t => drawListener(L, t) });
  for (const P of PHOTOGS) SQUARE.push({ y: P.y, rank: P.rank, draw: t => drawPhotog(P, t) });
  for (const L of LAMPS) { const draw = t => drawLamp(L, t); SQUARE.push({ y: L.y, rank: 0, draw, bare: draw }); }
  KIDS.forEach((K, i) => SQUARE.push({ y: K.y, rank: K.rank, draw: t => drawKid(K, i, t) }));
  const pillar = () => ctx.drawImage(PILLAR_C, PILLAR.x - 5, PILLAR.y - 28);
  SQUARE.push({ y: BUSK.y, rank: 0, draw: drawBusker }, { y: DANCE.y, rank: 0, draw: drawDance }, { y: BUB.y, rank: 0, draw: drawBubbleArtist },
    { y: PRETZ.y, rank: 0, draw: drawPretzel, bare: t => drawPretzel(t, true) },
    { y: CART.y, rank: 0, draw: drawCart, bare: t => drawCart(t, true) }, { y: WURST.y, rank: 0, draw: drawWurst }, { y: PILLAR.y, rank: 0, draw: pillar, bare: pillar },
    { y: PEDI_Y, rank: 0, draw: drawPedicab }, { y: TOUR.y, rank: 0, draw: drawTour }, { y: BAND_Y, rank: 0, draw: drawBand });
  SQUARE.sort((a, b) => a.y - b.y);
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) or the website's faces; billboard: false leaves the banner and its
// lamps out (the scaffold stays: the house is being renovated either way); weather: a WEATHERS pick, the city's own
// when left out; crowd: the share of the people shown, 0 to 1 (CROWD when left out or not a number; 0 = nobody)
function renderFrame(t, { pitch = false, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  const crowd = clamp(Number.isFinite(share) ? share : CROWD, 0, 1);
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  drawSky(t);
  if (wx.leaves) drawLeaves(0, t);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  // beyond the gate, the square's paving, the houses, the gate and its mirror image
  ctx.drawImage(FAR_C, 0, 0);
  drawFarLife(t, crowd);
  ctx.drawImage(GROUND_C, 0, 0);
  ctx.drawImage(HOUSE_C, 0, 0);
  drawHouseLife(t, crowd);
  if (billboard) drawBanner(t, pitch);
  drawGate(t, reducedMotion);
  drawRoofCrows(t, crowd === 0);
  drawMirror();
  drawPools(t);
  // the crowd, far to near; whoever's rank is not below the crowd's share stays home, and at 0 nobody is out at all:
  // only what stands there on its own is drawn (each entry's `bare`)
  if (crowd > 0) for (let i = 0; i < SQUARE.length; i++) { const e = SQUARE[i]; if (e.rank === 0 || e.rank < crowd) e.draw(t); }
  else for (let i = 0; i < SQUARE.length; i++) { const e = SQUARE[i]; if (e.bare) e.bare(t); }
  drawConfetti(t);
  if (wx.leaves) drawLeaves(1, t);
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawLindens(t);
  if (wx.leaves) drawLeaves(2, t);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'berlin',
  name: 'Berlin',
  tagline: 'Light festival at the Gate',
  timeZone: 'Europe/Berlin',
  accent: '#a8f000',                                  // light-show lime, written out: tools/seo/seo.mjs reads it from the source
  focusX: CX,
  landmark: [GX0, GX1],                               // the gate's central block under the Quadriga (the wings reach 83-396)
  landmarkTop: QTIP,                                  // the row of the eagle on the Quadriga's standard
  billboard: BB,
  crowd: CROWD,                                       // the default share of the people on the square; render(t, { crowd }) takes another
  ambience: { crowd: 0.5, wind: 0.14, traffic: 0.08 },
  events: [
    { t: PEDI[0].a + 1, type: 'chime', x: 30, gain: 0.25 },                 // the pedicab's bell
    { t: POLICE.a, type: 'siren', dur: 12, pan: [0.4, -0.5], gain: 0.16 },  // a police van on Ebertstrasse, behind the gate
    { t: PEDI[1].a + 1, type: 'chime', x: 450, gain: 0.25 },
    { t: TOUR.a + 1.5, type: 'chime', x: 30, gain: 0.3 },                   // the bike tour's guide rings
    ...[0, 4, 8, 12].map(k => ({ t: +(FINALE.a + k * CUT).toFixed(2), type: 'chime', x: CX, gain: k ? 0.4 : 0.6 })),   // the finale's cuts
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
